<template>
  <div class="ud-root" :class="{ 'ud-readonly': readOnly }">
    <!-- ============= HEAD (avatar + name/email/id) ============= -->
    <div class="sp-head">
      <div class="sp-client">
        <div class="sp-avatar">{{ headerInitials }}</div>
        <div class="sp-client-info">
          <div class="sp-name">{{ headerDisplayName }}</div>
          <div class="sp-email">{{ headerEmail || '—' }}</div>
          <div class="sp-id">ID: {{ (loadedUserData && loadedUserData.id) || resolvedUserId || '—' }}</div>
        </div>
      </div>
    </div>

    <!-- ============= STATUS BAR (4 cells) ============= -->
    <div class="sp-status">
      <div class="sp-stat">
        <label>Статус идентификации</label>
        <div class="val" :class="statusValClass">{{ identificationStatusLabel }}</div>
      </div>
      <div class="sp-stat">
        <label>Классификация</label>
        <div class="val">{{ shortClassificationLabel }}</div>
      </div>
      <div class="sp-stat">
        <label>Риск / Скоринг</label>
        <div class="val" :class="effectiveRiskClass">{{ effectiveRisk }}<span v-if="riskIsOverridden" class="risk-manual-tag">вручную</span></div>
      </div>
      <div class="sp-stat">
        <label>Лимит/мес</label>
        <div class="val">{{ (loadedUserData.limitInvestment || 0) | money }} $</div>
      </div>
    </div>

    <!-- ============= TABS ============= -->
    <div class="sp-tabs">
      <div
        v-for="t in visibleTabs"
        :key="t.key"
        class="sp-tab"
        :class="{ active: activeTab === t.key, 'sp-tab-right': t.right }"
        @click="setTab(t)"
      >{{ t.label }}</div>
    </div>

    <!-- ============= BODY ============= -->
    <div class="sp-body">
      <!-- IDENTIFICATION — 1:1 port of KYC_interactive #t-ident.
           Five sections in mock order: кириллица, латиница, контакты,
           статус и классификация, ПДЛ. Latin-script names come from
           AMLBot's latin pair (firstNameLatin / lastNameLatin /
           thirdNameLatin) when stored; otherwise show "не заполнено". -->
      <div v-show="activeTab === 'ident'">
        <section class="sp-section">
          <div class="sp-section-title">Личные данные — кириллица</div>
          <div class="sp-fields">
            <div class="sf"><label>Фамилия</label><div class="fv" :class="emptyClass(userForm.lastName)">{{ userForm.lastName || 'не заполнено' }}</div></div>
            <div class="sf"><label>Имя</label><div class="fv" :class="emptyClass(userForm.firstName)">{{ userForm.firstName || 'не заполнено' }}</div></div>
            <div class="sf"><label>Отчество</label><div class="fv" :class="emptyClass(userForm.surName)">{{ userForm.surName || 'не заполнено' }}</div></div>
            <div class="sf"><label>Дата рождения</label><div class="fv" :class="emptyClass(userForm.birthDate)">{{ formatDateOnly(userForm.birthDate) || 'не заполнено' }}</div></div>
            <div class="sf full"><label>Место рождения</label><div class="fv" :class="emptyClass(userForm.birthPlace)">{{ countryLabel(userForm.birthPlace) || 'не заполнено' }}</div></div>
            <div class="sf"><label>ИИН</label><div class="fv" :class="emptyClass(userForm.iin)">{{ userForm.iin || 'не заполнено' }}</div></div>
            <div class="sf"><label>Гражданство</label><div class="fv" :class="emptyClass(userForm.citizen)">{{ countryLabel(userForm.citizen) || 'не заполнено' }}</div></div>
          </div>
        </section>

        <!-- "Личные данные — латиница" блок временно скрыт: latin-pair
             still isn't persisted on the backend. Resurrect together
             with the latinFirstName/LastName/ThirdName computeds once
             AMLBot's bare first_name/last_name lands on lms_core_user. -->

        <section class="sp-section">
          <div class="sp-section-title">Контакты</div>
          <div class="sp-fields">
            <div class="sf"><label>Email</label><div class="fv">{{ userForm.email || '—' }}</div></div>
            <div class="sf"><label>Телефон</label><div class="fv" :class="emptyClass(userForm.phoneNumber)">{{ userForm.phoneNumber || 'не заполнено' }}</div></div>
            <div class="sf full"><label>Резидентство</label><div class="fv" :class="emptyClass(userForm.residencyCountry)">{{ countryLabel(userForm.residencyCountry) || 'не заполнено' }}</div></div>
          </div>
        </section>

        <section class="sp-section">
          <div class="sp-section-title">Статус и классификация</div>
          <div class="sp-fields">
            <div class="sf"><label>Тип клиента</label><div class="fv">{{ (loadedUserData && loadedUserData.isOrg) ? 'Юридическое лицо' : 'Физическое лицо' }}</div></div>
            <div class="sf"><label>Статус аккаунта</label><div class="fv">{{ identificationStatusLabel }}</div></div>
            <div class="sf"><label>Уровень риска</label><div class="fv" :class="emptyClass(identRiskLevel)">{{ identRiskLevel || 'не заполнено' }}</div></div>
            <div class="sf"><label>Скоринг</label><div class="fv empty">не заполнено</div></div>
            <div class="sf"><label>Лимит/месяц</label><div class="fv">{{ (loadedUserData && loadedUserData.limitInvestment) || 0 }} $</div></div>
            <div class="sf"><label>Результат тестирования</label><div class="fv" :class="emptyClass(identTestResult)">{{ identTestResult || 'не заполнено' }}</div></div>
            <div class="sf"><label>Дата создания</label><div class="fv" :class="emptyClass(loadedUserData && loadedUserData.registeredAt)">{{ formatDocDate(loadedUserData && loadedUserData.registeredAt) || 'не заполнено' }}</div></div>
            <div class="sf"><label>ID аккаунта</label><div class="fv">{{ (loadedUserData && loadedUserData.id) || '—' }}</div></div>
            <div class="sf"><label>Дата договора</label><div class="fv empty">не заполнено</div></div>
            <div class="sf"><label>Номер договора</label><div class="fv empty">не заполнено</div></div>
          </div>
        </section>

        <section class="sp-section">
          <div class="sp-section-title">ПДЛ / ИПДЛ</div>
          <div class="sp-fields">
            <div class="sf"><label>Причастность к ИПДЛ</label><div class="fv">{{ pepLabel(userForm.isPep) }}</div></div>
            <div class="sf"><label>Должность ПДЛ</label><div class="fv" :class="emptyClass(userForm.pepPosition)">{{ userForm.pepPosition || 'не заполнено' }}</div></div>
            <div class="sf full"><label>ФИО ИПДЛ</label><div class="fv" :class="emptyClass(userForm.pepFio)">{{ userForm.pepFio || 'не заполнено' }}</div></div>
          </div>
        </section>
      </div>

      <!-- DOCUMENT -->
      <div v-show="activeTab === 'doc'">
        <section class="sp-section">
          <div class="sp-section-title">Удостоверяющий документ</div>
          <div class="sp-fields">
            <div class="sf"><label>Тип документа</label><div class="fv" :class="emptyClass(userForm.docTypeId)">{{ userForm.docTypeId || 'не заполнено' }}</div></div>
            <div class="sf"><label>Серия</label><div class="fv" :class="emptyClass(userForm.kycSerial)">{{ userForm.kycSerial || 'не заполнено' }}</div></div>
            <div class="sf"><label>Номер</label><div class="fv" :class="emptyClass(userForm.kycNumber)">{{ userForm.kycNumber || 'не заполнено' }}</div></div>
            <div class="sf full"><label>Кем выдан</label><div class="fv" :class="emptyClass(userForm.kycIssuedBy)">{{ userForm.kycIssuedBy || 'не заполнено' }}</div></div>
            <div class="sf"><label>Дата выдачи</label><div class="fv" :class="emptyClass(userForm.kycGivenDate)">{{ formatDateOnly(userForm.kycGivenDate) || 'не заполнено' }}</div></div>
            <div class="sf"><label>Срок действия</label><div class="fv" :class="emptyClass(userForm.kycTillDate)">{{ formatDateOnly(userForm.kycTillDate) || 'не заполнено' }}</div></div>
          </div>
        </section>

        <!-- Сканы документов идентификации, зеркалированные с AMLBot
             (удостоверение личности, подтверждение адреса, селфи). -->
        <section class="sp-section">
          <div class="sp-section-title">Документы идентификации (AMLBot)</div>
          <div v-if="kycFilesLoading" class="sp-empty">Загрузка…</div>
          <div v-else-if="!kycFiles.length" class="sp-empty">Документы идентификации не найдены</div>
          <div v-else>
            <div v-for="grp in kycFileGroups" :key="grp.type" class="kyc-doc-group">
              <div class="kyc-doc-group-title">{{ kycDocTypeLabel(grp.type) }}</div>
              <div class="kyc-doc-grid">
                <div
                  v-for="f in grp.files"
                  :key="f.fileId"
                  class="kyc-doc-file"
                  :title="kycSideLabel(f.fileSide) + ' · ' + (f.mimeType || '')"
                  @click="viewKycFile(f)"
                >
                  <img v-if="kycThumbs[f.fileId]" :src="kycThumbs[f.fileId]" class="kyc-doc-thumb-img" alt="" />
                  <div v-else class="kyc-doc-thumb-ph">{{ kycFileExt(f) }}</div>
                  <div class="kyc-doc-file-side">{{ kycSideLabel(f.fileSide) }}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- ADDRESS -->
      <div v-show="activeTab === 'addr'">
        <section class="sp-section">
          <div class="sp-section-title">Адрес регистрации</div>
          <div class="sp-fields">
            <div class="sf"><label>Страна</label><div class="fv" :class="emptyClass(userForm.regCountry)">{{ countryLabel(userForm.regCountry) || 'не заполнено' }}</div></div>
            <div class="sf"><label>Индекс</label><div class="fv" :class="emptyClass(userForm.regIndex)">{{ userForm.regIndex || 'не заполнено' }}</div></div>
            <div class="sf"><label>Регион</label><div class="fv" :class="emptyClass(userForm.regRegion)">{{ userForm.regRegion || 'не заполнено' }}</div></div>
            <div class="sf"><label>Город</label><div class="fv" :class="emptyClass(userForm.regCity)">{{ userForm.regCity || 'не заполнено' }}</div></div>
            <div class="sf full"><label>Адрес</label><div class="fv" :class="emptyClass(userForm.regAddr)">{{ userForm.regAddr || 'не заполнено' }}</div></div>
          </div>
        </section>

        <section class="sp-section">
          <div class="sp-section-title">Адрес фактического проживания</div>
          <div class="sp-fields">
            <div class="sf"><label>Страна</label><div class="fv" :class="emptyClass(userForm.realCountry)">{{ countryLabel(userForm.realCountry) || 'не заполнено' }}</div></div>
            <div class="sf"><label>Индекс</label><div class="fv" :class="emptyClass(userForm.realIndex)">{{ userForm.realIndex || 'не заполнено' }}</div></div>
            <div class="sf"><label>Регион</label><div class="fv" :class="emptyClass(userForm.realRegion)">{{ userForm.realRegion || 'не заполнено' }}</div></div>
            <div class="sf"><label>Город</label><div class="fv" :class="emptyClass(userForm.realCity)">{{ userForm.realCity || 'не заполнено' }}</div></div>
            <div class="sf full"><label>Адрес</label><div class="fv" :class="emptyClass(userForm.realAddr)">{{ userForm.realAddr || 'не заполнено' }}</div></div>
          </div>
        </section>
      </div>

      <!-- FINANCE — 1:1 port of KYC_interactive #t-finance.
           Two sections: Занятость (place + role) and Источник
           финансирования (kind + detail). The two extra fields we used
           to show — Характер and Цель деловых отношений — moved into
           an optional third section that only appears when populated,
           so the default layout matches the mock. -->
      <div v-show="activeTab === 'finance'">
        <section class="sp-section">
          <div class="sp-section-title">Занятость</div>
          <div class="sp-fields">
            <div class="sf full"><label>Место работы</label><div class="fv" :class="emptyClass(userForm.jobAddr)">{{ userForm.jobAddr || 'не заполнено' }}</div></div>
            <div class="sf"><label>Должность</label><div class="fv" :class="emptyClass(userForm.jobTitle)">{{ userForm.jobTitle || 'не заполнено' }}</div></div>
          </div>
        </section>

        <section class="sp-section">
          <div class="sp-section-title">Источник финансирования</div>
          <div class="sp-fields">
            <div class="sf"><label>Вид источника</label><div class="fv" :class="emptyClass(userForm.moneySource)">{{ userForm.moneySource || 'не заполнено' }}</div></div>
            <div class="sf full"><label>Характеристика</label><div class="fv" :class="emptyClass(userForm.wealthSource)">{{ userForm.wealthSource || 'не заполнено' }}</div></div>
          </div>
        </section>

        <section v-if="hasBusinessFinance" class="sp-section">
          <div class="sp-section-title">Деловые отношения</div>
          <div class="sp-fields">
            <div class="sf"><label>Характер</label><div class="fv" :class="emptyClass(userForm.jobBusiness)">{{ businessLabel(userForm.jobBusiness) }}</div></div>
            <div class="sf full"><label>Цель</label><div class="fv" :class="emptyClass(userForm.jobBusinessPurpose)">{{ userForm.jobBusinessPurpose || 'не заполнено' }}</div></div>
          </div>
        </section>
      </div>

      <!-- FILES -->
      <div v-show="activeTab === 'files'">
        <section class="sp-section">
          <div class="sp-section-title">Загруженные документы</div>
          <div v-if="docsLoading" class="sp-empty">Загрузка…</div>
          <div v-else-if="!docs.length" class="sp-empty">Документы не загружены</div>
          <div v-else>
            <div
              v-for="d in docs"
              :key="d.docId"
              class="doc-item"
              @click="downloadDoc(d)"
            >
              <div class="doc-thumb">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="5" y="2" width="14" height="20" rx="2" />
                  <line x1="9" y1="7" x2="15" y2="7" />
                  <line x1="9" y1="11" x2="15" y2="11" />
                  <line x1="9" y1="15" x2="12" y2="15" />
                </svg>
              </div>
              <div class="doc-info">
                <div class="doc-name">{{ docTypeLabel(d.docType) }}</div>
                <div class="doc-meta">{{ docTrack(d.docType).label }} · {{ formatDocDate(d.createDate) }}</div>
              </div>
              <span class="doc-status" :class="docStatusClass(d.docStatus)">{{ docStatusLabel(d.docStatus) }}</span>
            </div>
          </div>
        </section>
      </div>

      <!-- AML — 1:1 port of KYC_interactive #t-aml.
           Three sections: AML-профиль, Мониторинг, Изменить риск.
           Manual-override block (3-я секция) is UI-only at the moment —
           there is no backend endpoint yet for admin risk overrides,
           so submit fires a toast. -->
      <div v-show="activeTab === 'aml'">
        <section class="sp-section">
          <div class="sp-section-title">AML-профиль</div>
          <div class="sp-fields">
            <div class="sf"><label>AML-флаги</label><div class="fv" :class="amlFlagsClass">{{ amlFlagsLabel }}</div></div>
            <div class="sf"><label>Скоринг риска</label><div class="fv" :class="effectiveRiskClass">{{ effectiveRisk }}<span v-if="riskIsOverridden" class="risk-manual-tag">вручную</span></div></div>
            <div v-if="riskIsOverridden" class="sf full"><label>Переопределено вручную</label>
              <div class="fv">
                {{ riskOverrideCurrent.byName || riskOverrideCurrent.byEmail || 'оператор' }} · {{ formatScreeningDate(riskOverrideCurrent.createdAt) }}
                <template v-if="riskOverrideCurrent.reason"> — «{{ riskOverrideCurrent.reason }}»</template>
              </div>
            </div>
            <div class="sf full"><label>Причина высокого риска</label>
              <div class="fv" :class="amlHighRiskReason === 'Не выявлено' ? 'empty' : ''">
                {{ amlHighRiskReason === 'Не выявлено' ? 'не заполнено' : amlHighRiskReason }}
              </div>
            </div>
          </div>
        </section>

        <section class="sp-section">
          <div class="sp-section-title">Мониторинг</div>
          <div class="sp-fields">
            <div class="sf"><label>Статус алерта</label><div class="fv empty">нет алертов</div></div>
            <div class="sf"><label>Дата последнего скрининга</label><div class="fv" :class="emptyClass(loadedUserData && loadedUserData.lastKycScreenedAt)">{{ formatScreeningDate(loadedUserData && loadedUserData.lastKycScreenedAt) }}</div></div>
            <div class="sf full"><label>Результаты мониторинга операций</label><div class="fv empty">не заполнено</div></div>
          </div>
        </section>

        <section class="sp-section">
          <div class="sp-section-title">Изменить риск</div>
          <div style="margin-bottom:12px">
            <select v-model="amlRiskOverride" class="action-select">
              <option value="">— выберите риск —</option>
              <option value="m">Средний</option>
              <option value="h">Высокий</option>
              <option value="c">Критический</option>
            </select>
          </div>
          <div style="margin-bottom:8px">
            <span class="action-label">Комментарий</span>
            <textarea
              v-model="amlRiskComment"
              class="comment-area"
              placeholder="Причина изменения уровня риска..."
            ></textarea>
          </div>
          <button
            type="button"
            class="action-save-btn"
            :disabled="!amlRiskOverride || !amlRiskComment.trim() || riskBusy"
            @click="saveAmlRiskOverride"
          >
            <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
            Сохранить изменения
          </button>

          <div v-if="riskLog.length" class="risk-history">
            <div class="risk-history-title">История переопределений</div>
            <div v-for="entry in riskLog" :key="entry.id" class="risk-history-item">
              <div class="risk-history-top">
                <span class="risk-history-level" :class="riskLevelClass(entry.riskLevel)">{{ riskLevelLabel(entry.riskLevel) }}</span>
                <span class="risk-history-date">{{ formatScreeningDate(entry.createdAt) }}</span>
              </div>
              <div class="risk-history-reason">«{{ entry.reason }}»</div>
              <div class="risk-history-actor">{{ entry.byName || entry.byEmail || 'оператор' }}</div>
            </div>
          </div>
        </section>
      </div>

      <!-- REQUESTS — 1:1 with KYC_interactive #t-requests.
           Active block (PENDING / DRAFT) up top, historical block
           (APPROVED / REJECTED) underneath in a separate section. -->
      <div v-show="activeTab === 'requests'">
        <section class="sp-section" style="padding-top:4px">
          <div v-if="requestsLoading" class="sp-empty">Загрузка…</div>
          <div v-else-if="!requests.length" class="no-req">Активных заявок нет</div>
          <div v-else>
            <!-- Active classification requests -->
            <div v-if="!activeRequests.length" class="no-req">Активных заявок нет</div>
            <div
              v-for="r in activeRequests"
              :key="'active-req-' + r.requestId"
              class="req-list-item"
              @click="openRequestModal(r)"
            >
              <div
                class="req-list-icon"
                :style="{ background: requestIconBg(r), color: requestIconColor(r) }"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="23 4 23 10 17 10"/>
                  <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                </svg>
              </div>
              <div class="req-list-body">
                <div class="req-list-title">
                  Заявка на реклассификацию
                  <span
                    class="req-type-badge"
                    :style="{ background: requestIconBg(r), color: requestIconColor(r) }"
                  >{{ shortRequestPath(r) }}</span>
                </div>
                <div class="req-list-meta">
                  <span>{{ requestListSubLabel(r) }}</span>
                  · <span :style="{ color: requestIconColor(r) }">{{ requestStatusLabel(r.status) }}</span>
                </div>
              </div>
              <div class="req-list-arrow">›</div>
            </div>
            <!-- Историю (APPROVED / REJECTED) здесь не показываем —
                 она доступна во вкладке «Квалификация». -->
          </div>
        </section>
      </div>

      <!-- TESTS -->
      <div v-show="activeTab === 'tests'">
        <section class="sp-section">
          <div class="sp-section-title">История тестирования</div>
          <div v-if="attemptsLoading" class="sp-empty">Загрузка…</div>
          <div v-else-if="!attempts.length" class="sp-empty">Пользователь ещё не проходил тест</div>
          <div v-else>
            <div
              v-for="a in attempts"
              :key="a.attemptId"
              class="attempt-block"
              :class="{ 'attempt-block-open': attemptOpen[a.attemptId] }"
            >
              <div class="attempt-row" @click="toggleAttempt(a)">
                <div class="attempt-row-main">
                  <div class="attempt-title">{{ testCodeLabel(a.testCode) }}</div>
                  <div class="attempt-meta">{{ formatDocDate(a.startedAt) }} · {{ a.score != null ? (a.score + '/' + a.totalQuestionCount) : '—' }}</div>
                </div>
                <span class="attempt-verdict" :class="attemptVerdictClass(a)">{{ attemptVerdictLabel(a) }}</span>
                <span class="attempt-chevron" aria-hidden="true">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </span>
              </div>
              <div v-if="attemptOpen[a.attemptId]" class="attempt-answers">
                <div v-if="attemptDetailsLoading[a.attemptId]" class="sp-empty">Загрузка…</div>
                <div v-else-if="!(attemptDetails[a.attemptId] && attemptDetails[a.attemptId].length)" class="sp-empty">
                  Ответы недоступны
                </div>
                <ol v-else class="answer-list">
                  <li
                    v-for="(q, idx) in attemptDetails[a.attemptId]"
                    :key="(a.attemptId + '-' + idx)"
                    class="answer-item"
                    :class="answerItemClass(a, q)"
                  >
                    <div class="answer-q">{{ q.questionText || ('Вопрос #' + (idx + 1)) }}</div>
                    <div class="answer-a">{{ q.chosenAnswerText || '—' }}</div>
                    <div
                      v-if="a.testCode === 'PROFESSIONAL' && q.isProfile"
                      class="answer-profile"
                    >
                      Профильный вопрос — не оценивается
                    </div>
                    <div
                      v-else-if="answerHasVerdict(a, q) && q.correctAnswerText && q.isCorrect !== 1"
                      class="answer-correct"
                    >
                      Верный ответ: {{ q.correctAnswerText }}
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- QUAL — 1:1 port of KYC_interactive #t-qual.
           Three sections: текущая классификация, тестирование (collapsible
           q-test-items), история реклассификаций (q-recls-items with the
           two-vote compliance + MLRO grid). -->
      <div v-show="activeTab === 'qual'">
        <!-- Section 1 — Current classification + first-time-in-tier
             timeline. Each row is a step up the ladder (NQ → Q →
             Professional) with the date the user FIRST landed there
             and the actor who flipped them. -->
        <section class="sp-section">
          <div class="sp-section-title">Текущая классификация</div>
          <div class="sp-fields">
            <div class="sf">
              <label>Классификация</label>
              <div class="fv">{{ classificationStatusLabel }}</div>
            </div>
            <div class="sf">
              <label>Присвоена</label>
              <div class="fv" :class="emptyClass(qualAssignedAt)">
                {{ qualAssignedAt ? formatDocDate(qualAssignedAt) : '—' }}
              </div>
            </div>
            <div class="sf full">
              <label>Основание</label>
              <div class="fv" :class="emptyClass(qualBasis)">{{ qualBasis || '—' }}</div>
            </div>
          </div>

        </section>

        <!-- Section 2 — Тестирование и опросники -->
        <section class="sp-section">
          <div class="sp-section-title">Тестирование и опросники</div>
          <div v-if="attemptsLoading" class="sp-empty">Загрузка…</div>
          <div v-else-if="!attempts.length" class="no-qual">Тесты ещё не проходились</div>
          <div v-else>
            <div
              v-for="a in attempts"
              :key="'qt-' + a.attemptId"
              class="q-test-item"
            >
              <div class="q-test-header" @click="toggleQTest(a.attemptId)">
                <div>
                  <div class="q-test-title">{{ testCodeLabel(a.testCode) }}</div>
                  <div class="q-test-meta">
                    {{ formatDocDate(a.startedAt) }}
                    <span v-if="a.score != null"> · {{ a.score }}/{{ a.totalQuestionCount }}</span>
                    <span v-else> · —</span>
                  </div>
                </div>
                <div class="q-test-right">
                  <span :class="qTestBadgeClass(a)">{{ qTestBadgeLabel(a) }}</span>
                  <span class="q-chevron" :class="{ open: qTestOpen[a.attemptId] }">▾</span>
                </div>
              </div>
              <div class="q-test-body" :class="{ open: qTestOpen[a.attemptId] }">
                <!-- Header summary cards (score + date) — kept slim so
                     the bigger answer list below is the focus. -->
                <div class="q-test-fields">
                  <div v-if="a.score != null" class="qtf">
                    <label>Результат</label>
                    <div class="qv">{{ a.score }}/{{ a.totalQuestionCount }}</div>
                  </div>
                  <div class="qtf">
                    <label>Дата прохождения</label>
                    <div class="qv">{{ formatDocDate(a.startedAt) }}</div>
                  </div>
                </div>

                <!-- Per-question detail — same data the "Тестирование"
                     tab renders. attemptDetails is a shared cache, so
                     opening one tab populates the other for free. -->
                <div v-if="attemptDetailsLoading[a.attemptId]" class="sp-empty">Загрузка…</div>
                <div
                  v-else-if="!(attemptDetails[a.attemptId] && attemptDetails[a.attemptId].length)"
                  class="sp-empty"
                >Ответы недоступны</div>
                <ol v-else class="answer-list q-test-answers">
                  <li
                    v-for="(q, idx) in attemptDetails[a.attemptId]"
                    :key="'qq-' + a.attemptId + '-' + idx"
                    class="answer-item"
                    :class="answerItemClass(a, q)"
                  >
                    <div class="answer-q">{{ q.questionText || ('Вопрос #' + (idx + 1)) }}</div>
                    <div class="answer-a">{{ q.chosenAnswerText || '—' }}</div>
                    <div
                      v-if="a.testCode === 'PROFESSIONAL' && q.isProfile"
                      class="answer-profile"
                    >
                      Профильный вопрос — не оценивается
                    </div>
                    <div
                      v-else-if="answerHasVerdict(a, q) && q.correctAnswerText && q.isCorrect !== 1"
                      class="answer-correct"
                    >
                      Верный ответ: {{ q.correctAnswerText }}
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        <!-- Section 3 — История реклассификаций -->
        <section class="sp-section">
          <div class="sp-section-title">История реклассификаций</div>
          <div v-if="requestsLoading" class="sp-empty">Загрузка…</div>
          <div v-else-if="!requests.length" class="no-qual">Заявок на реклассификацию не было</div>
          <div v-else>
            <div
              v-for="r in qualReclsList"
              :key="'qr-' + r.requestId"
              class="q-recls-item"
            >
              <div class="q-recls-header">
                <div class="q-recls-title">{{ shortRequestPath(r) }}</div>
                <span class="q-recls-result" :class="qReclsResultClass(r)">{{ qReclsResultLabel(r) }}</span>
              </div>
              <div class="q-recls-date">Подана: {{ formatDocDate(r.createdAt) || '—' }}</div>
              <button
                type="button"
                class="hist-open-btn"
                style="margin-bottom:10px"
                @click="openRequestModal(r)"
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                </svg>
                Документы заявки
              </button>
              <div class="q-recls-votes">
                <div class="q-vote" :style="{ borderColor: qVoteBorder(r, 'compliance') }">
                  <div class="q-vote-role">Комплаенс-офицер</div>
                  <template v-if="qVote(r, 'compliance')">
                    <div class="q-vote-result" :style="{ color: qVoteColor(r, 'compliance') }">
                      {{ qVote(r, 'compliance').action === 'approve' ? '✓ Одобрил' : '✕ Отклонил' }}
                    </div>
                    <div class="q-vote-comment">{{ qVote(r, 'compliance').comment }}</div>
                    <div v-if="qVote(r, 'compliance').internal" class="q-vote-comment q-vote-internal">Внутренняя (не видна клиенту): {{ qVote(r, 'compliance').internal }}</div>
                    <div class="q-vote-when">{{ qVote(r, 'compliance').when }}</div>
                  </template>
                  <div v-else-if="!requestNeedsCompliance(r)" class="q-vote-pending">Не требуется (путь через тест)</div>
                  <div v-else class="q-vote-pending">Ожидает решения</div>
                </div>
                <div class="q-vote" :style="{ borderColor: qVoteBorder(r, 'mlro') }">
                  <div class="q-vote-role">MLRO</div>
                  <template v-if="qVote(r, 'mlro')">
                    <div class="q-vote-result" :style="{ color: qVoteColor(r, 'mlro') }">
                      {{ qVote(r, 'mlro').action === 'approve' ? '✓ Одобрил' : '✕ Отклонил' }}
                    </div>
                    <div class="q-vote-comment">{{ qVote(r, 'mlro').comment }}</div>
                    <div v-if="qVote(r, 'mlro').internal" class="q-vote-comment q-vote-internal">Внутренняя (не видна клиенту): {{ qVote(r, 'mlro').internal }}</div>
                    <div class="q-vote-when">{{ qVote(r, 'mlro').when }}</div>
                  </template>
                  <div v-else class="q-vote-pending">Ожидает решения</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- ACTIONS — 1:1 port of KYC_interactive #t-actions.
           Single sp-section with two stacked subsections:
             1) 2FA reset button + history list
             2) Block/Unblock button + journal
           "Карточка профиля (история)" перенесена в будущий "History"
           tab. -->
      <div v-show="activeTab === 'actions'">
        <section class="sp-section">
          <div class="sp-section-title" style="margin-bottom:10px">Двухфакторная аутентификация</div>
          <button type="button" class="twofa-btn" @click="openResetTwoFAModal">
            <svg viewBox="0 0 24 24">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Сбросить 2FA
          </button>
          <div v-if="twoFaResetLog.length" class="twofa-log">
            <div class="twofa-log-title">История сбросов 2FA</div>
            <div
              v-for="(entry, idx) in twoFaResetLog"
              :key="'2fa-log-' + idx"
              class="twofa-log-item"
            >
              <div class="twofa-log-date">{{ formatSnapshotDate(entry.at) }}</div>
              <div class="twofa-log-admin">{{ entry.by }}</div>
              <div v-if="entry.comment" class="twofa-log-comment">{{ entry.comment }}</div>
            </div>
          </div>

          <!-- Block / Unblock — separated visually by twofa-section
               (top border) as in the mock. -->
          <div class="twofa-section">
            <div class="sp-section-title" style="margin-bottom:10px">Блокировка / Разблокировка</div>
            <div class="block-btn-wrap">
              <button
                v-if="isUserActive"
                type="button"
                class="block-btn-do bb-do-block"
                @click="openBlockModal"
              >
                <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
                Заблокировать
              </button>
              <button
                v-else
                type="button"
                class="block-btn-do bb-do-unblock"
                @click="openBlockModal"
              >
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                Разблокировать
              </button>
            </div>
            <div class="sp-section-title" style="margin-top:12px;margin-bottom:8px">Журнал блокировок</div>
            <div class="block-log">
              <div v-if="blockLog.length">
                <div
                  v-for="(entry, idx) in blockLog"
                  :key="'block-log-' + idx"
                  class="blog-item"
                >
                  <div class="blog-item-head">
                    <span class="blog-type" :class="'blog-type-' + entry.type" :style="{ color: blogColor(entry.type) }">
                      <span class="blog-dot" :style="{ background: blogColor(entry.type) }"></span>
                      {{ blogLabel(entry.type) }}
                    </span>
                    <span class="blog-date">{{ formatSnapshotDate(entry.at) }}</span>
                  </div>
                  <div class="blog-admin">{{ entry.by }}</div>
                  <div v-if="entry.reason" class="blog-reason">{{ entry.reason }}</div>
                </div>
              </div>
              <div v-else class="no-log">Блокировок и заморозок не было</div>
            </div>
          </div>

          <!-- Заморозка аккаунта — мера по результатам проверки (отдельно от
               блокировки): клиент входит в ЛК, но сервисы недоступны. -->
          <div class="twofa-section">
            <div class="sp-section-title" style="margin-bottom:10px">Заморозка аккаунта</div>
            <p class="freeze-note">
              При заморозке клиент сохраняет доступ к личному кабинету, но
              полностью ограничен в сервисах (пополнение и др.). Клиенту
              направляется уведомление.
            </p>
            <div class="block-btn-wrap">
              <button
                v-if="!isAccountFrozen"
                type="button"
                class="block-btn-do bb-do-freeze"
                @click="openFreezeModal"
              >
                <svg viewBox="0 0 24 24"><line x1="12" y1="2" x2="12" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="5" y1="5" x2="19" y2="19"/><line x1="19" y1="5" x2="5" y2="19"/></svg>
                Заморозить аккаунт
              </button>
              <button
                v-else
                type="button"
                class="block-btn-do bb-do-unblock"
                @click="openFreezeModal"
              >
                <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12"/></svg>
                Разморозить аккаунт
              </button>
              <span v-if="isAccountFrozen" class="freeze-state-badge">🔒 Аккаунт заморожен</span>
            </div>
          </div>
        </section>

        <!-- Block/unblock confirm modal — kept here so it's not in the
             snapshot DOM until needed. -->
        <b-modal
          id="block-user-modal-detail"
          ref="blockUserModal"
          :title="isUserActive ? 'Заблокировать пользователя' : 'Разблокировать пользователя'"
          :ok-title="isUserActive ? 'Заблокировать' : 'Разблокировать'"
          :ok-variant="isUserActive ? 'danger' : 'success'"
          cancel-title="Отмена"
          cancel-variant="outline-secondary"
          :ok-disabled="isUserActive ? (!blockReasonCode || !blockReason.trim()) : !blockReason.trim()"
          @ok="performToggleActive"
          centered
        >
          <p v-if="isUserActive" class="mb-2">
            Пользователь не сможет войти в систему. Действие можно
            отменить — позже разблокировав аккаунт.
          </p>
          <p v-else class="mb-2">
            Пользователь снова сможет входить и пользоваться системой.
          </p>
          <b-form-group
            v-if="isUserActive"
            label="Причина блокировки (обязательно)"
            label-class="kyc-filter-label"
            class="mt-2"
          >
            <b-form-select v-model="blockReasonCode" :options="blockReasonOptions">
              <template #first>
                <b-form-select-option :value="''" disabled>— выберите причину —</b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>
          <b-form-group
            :label="isUserActive ? 'Комментарий (обязательно)' : 'Причина разблокировки (обязательно)'"
            label-class="kyc-filter-label"
            class="mt-2"
          >
            <b-form-textarea
              v-model="blockReason"
              rows="3"
              :placeholder="isUserActive
                ? 'Комментарий для журнала…'
                : 'Опишите, почему вы разблокируете этот аккаунт…'"
            />
          </b-form-group>
        </b-modal>

        <!-- Заморозка / разморозка аккаунта: комментарий для внутреннего
             аудита обязателен в обоих случаях. -->
        <b-modal
          id="freeze-user-modal"
          ref="freezeUserModal"
          :title="isAccountFrozen ? 'Разморозить аккаунт' : 'Заморозить аккаунт'"
          :ok-title="isAccountFrozen ? 'Разморозить' : 'Заморозить'"
          :ok-variant="isAccountFrozen ? 'success' : 'warning'"
          cancel-title="Отмена"
          cancel-variant="outline-secondary"
          :ok-disabled="freezeBusy || !freezeReason.trim()"
          @ok.prevent="performToggleFreeze"
          centered
        >
          <p v-if="!isAccountFrozen" class="mb-2">
            Клиент сохранит доступ к личному кабинету, но будет полностью
            ограничен в сервисах (пополнение и др.). Ему уйдёт уведомление
            о заморозке. Действие обратимо — позже можно разморозить.
          </p>
          <p v-else class="mb-2">
            Клиенту снова станут доступны сервисы. Ему уйдёт уведомление о
            снятии заморозки.
          </p>
          <b-form-group
            label="Комментарий для внутреннего аудита (обязательно)"
            label-class="kyc-filter-label"
            class="mt-2"
          >
            <b-form-textarea
              v-model="freezeReason"
              rows="3"
              placeholder="Например: подозрительный источник средств по результатам KYT, тикет #123"
            />
          </b-form-group>
        </b-modal>

        <!-- Reject reclassification document: две причины — клиентская (уходит
             клиенту в письме) и внутренняя (для аудита, клиенту не видна).
             Обе обязательны. -->
        <b-modal
          id="reject-doc-modal"
          ref="rejectDocModal"
          title="Отклонить документ реклассификации"
          ok-title="Отклонить"
          ok-variant="danger"
          cancel-title="Отмена"
          cancel-variant="outline-secondary"
          :ok-disabled="rejectDocBusy || !rejectDocReason.trim() || !rejectDocInternal.trim()"
          @ok="confirmRejectDoc"
          centered
        >
          <p class="mb-2">
            Документ будет отклонён. Заявка на реклассификацию перейдёт в
            статус «Отклонена». Укажите две причины: одну — для клиента (уйдёт
            ему в уведомлении), другую — для внутреннего аудита (клиенту не видна).
          </p>
          <b-form-group
            label="Причина отказа для клиента (обязательно)"
            label-class="kyc-filter-label"
            class="mt-2"
          >
            <b-form-textarea
              v-model="rejectDocReason"
              rows="3"
              placeholder="Например: срок действия документа истёк; данные в документе не совпадают с анкетой…"
            />
          </b-form-group>
          <b-form-group
            label="Причина для внутреннего аудита — не видна клиенту (обязательно)"
            label-class="kyc-filter-label"
            class="mt-2"
          >
            <b-form-textarea
              v-model="rejectDocInternal"
              rows="3"
              placeholder="Служебная заметка для аудита: реальная причина отказа, тикет, детали проверки…"
            />
          </b-form-group>
        </b-modal>

        <!-- Approve SoF document with a MANUAL investment limit. Fires when
             MLRO approves the money-side doc (QUALIFIED/PROFESSIONAL_SOF);
             the limit is applied when the whole request is finalized. -->
        <b-modal
          id="approve-limit-modal"
          ref="approveLimitModal"
          title="Одобрить документ и задать лимит"
          ok-title="Одобрить"
          ok-variant="success"
          cancel-title="Отмена"
          cancel-variant="outline-secondary"
          :ok-disabled="approveDocBusy || approveLimitValue === '' || approveLimitValue === null || Number(approveLimitValue) < 0 || approveLimitOverMax"
          @ok="confirmApproveLimit"
          centered
        >
          <p class="mb-2">
            Документ источника средств будет одобрен. Укажите месячный лимит
            инвестиций для этого клиента — он применится при финализации
            заявки на реклассификацию (0 = без лимита).
          </p>
          <b-form-group
            :label="approveLimitMax != null ? ('Лимит инвестиций, $ / мес (макс. ' + formatMoney(approveLimitMax) + ')') : 'Лимит инвестиций, $ / мес'"
            label-class="kyc-filter-label"
            class="mt-2"
          >
            <b-form-input
              v-model="approveLimitValue"
              type="number"
              min="0"
              step="1000"
              :state="approveLimitOverMax ? false : null"
              placeholder="например, 250000"
            />
            <b-form-invalid-feedback :state="approveLimitOverMax ? false : null">
              Превышает максимально разрешённый лимит для этой классификации — {{ formatMoney(approveLimitMax) }} $/мес.
            </b-form-invalid-feedback>
          </b-form-group>
          <b-form-group
            label="Комментарий для внутреннего аудита (необязательно)"
            label-class="kyc-filter-label"
            class="mt-2"
          >
            <b-form-textarea
              v-model="approveComment"
              rows="2"
              placeholder="Служебная заметка для аудита — клиенту не видна"
            />
          </b-form-group>
        </b-modal>

        <!-- Одобрение не-SoF документа: необязательный комментарий для
             внутреннего аудита (клиенту не отправляется). -->
        <b-modal
          id="approve-comment-modal"
          ref="approveCommentModal"
          title="Одобрить документ"
          ok-title="Одобрить"
          ok-variant="success"
          cancel-title="Отмена"
          cancel-variant="outline-secondary"
          :ok-disabled="approveDocBusy"
          @ok="confirmApproveComment"
          centered
        >
          <p class="mb-2">
            Документ будет одобрен. При необходимости оставьте комментарий
            для внутреннего аудита — клиенту он не показывается.
          </p>
          <b-form-group
            label="Комментарий для внутреннего аудита (необязательно)"
            label-class="kyc-filter-label"
            class="mt-2"
          >
            <b-form-textarea
              v-model="approveComment"
              rows="3"
              placeholder="Служебная заметка для аудита — клиенту не видна"
            />
          </b-form-group>
        </b-modal>

        <!-- 2FA reset confirm modal (kept from the previous layout). -->
        <b-modal
          id="reset-2fa-modal"
          ref="resetTwoFAModal"
          title="Сброс 2FA"
          ok-title="Сбросить"
          ok-variant="danger"
          cancel-title="Отмена"
          cancel-variant="outline-secondary"
          @ok="performResetTwoFA"
          centered
        >
          <p class="mb-2">
            Текущая 2FA-настройка пользователя (секрет и все резервные
            коды) будет <strong>удалена без возможности восстановления</strong>.
          </p>
          <p class="mb-2">
            При следующем входе пользователь будет повторно проведён
            через обязательную настройку 2FA.
          </p>
          <b-alert show variant="warning" class="mb-0">
            <b-icon icon="exclamation-triangle" class="mr-2" />
            Делайте это только после того, как лично подтвердили
            личность владельца аккаунта.
          </b-alert>
        </b-modal>
      </div>
    </div>

    <!-- ============ REQUEST DETAILS MODAL ============ -->
    <transition name="req-modal-fade">
      <div
        v-if="requestModalOpen"
        class="req-modal-overlay show"
        @click.self="closeRequestModal"
      >
        <div class="req-modal" role="dialog" aria-modal="true">
          <header class="req-modal-head">
            <div>
              <div class="req-modal-title">Заявка на реклассификацию</div>
              <div class="req-modal-sub">
                Подана {{ formatDocDate(activeRequest && activeRequest.submittedAt) }} · Изучите все сведения перед принятием решения
              </div>
            </div>
            <button class="req-modal-close" aria-label="Закрыть" @click="closeRequestModal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </header>

          <div class="req-modal-body" v-if="activeRequest">
            <div class="req-modal-section full">
              <div class="req-modal-sec-title">Общая информация</div>
              <div class="req-modal-fields">
                <div class="rmf">
                  <label>Текущая классификация</label>
                  <div class="rv">{{ requestCategoryLabel(activeRequest.fromCategory) }}</div>
                </div>
                <div class="rmf">
                  <label>Запрашиваемая классификация</label>
                  <div class="rv arrow">→ {{ requestCategoryLabel(activeRequest.requestedCategory) }}</div>
                </div>
                <div class="rmf">
                  <label>Дата подачи</label>
                  <div class="rv">{{ formatDocDate(activeRequest.submittedAt) }}</div>
                </div>
                <div class="rmf">
                  <label>Статус заявки</label>
                  <div class="rv">{{ requestStatusLabel(activeRequest.status) }}</div>
                </div>
                <div class="rmf">
                  <label>{{ activeRequest.requestedCategory === 'PROFESSIONAL' ? 'Опросник' : 'Квалификационный тест' }}</label>
                  <div class="rv">
                    <span v-if="activeRequest.attemptId" class="rv-ok">✓ Пройден</span>
                    <span v-else class="rv-todo">Не пройден</span>
                  </div>
                </div>
                <div class="rmf">
                  <label>Основание</label>
                  <div class="rv">{{ requestBasisLabel(activeRequest) }}</div>
                </div>
              </div>
            </div>

            <!-- Documents grouped by semantic bucket + reviewer badge.
                 Источник средств → MLRO, Сертификат / История торгов →
                 Комплаенс-офицер. Badge mirrors KYC_interactive mock —
                 indigo для Compliance, green для MLRO. -->
            <div
              v-for="group in requestDocumentGroups(activeRequest)"
              :key="activeRequest.requestId + '-grp-' + group.label"
              class="req-modal-section full"
            >
              <div class="req-modal-sec-title req-modal-sec-title--with-badge">
                <span>{{ group.label }}</span>
                <span class="req-reviewer-badge" :class="'req-reviewer-badge--' + group.reviewerTone">{{ group.reviewerLabel }}</span>
              </div>
              <template v-for="type in group.docTypes">
                <!-- Empty slot when nothing is uploaded yet for this
                     required type. -->
                <div
                  v-if="!docsByType(activeRequest, type).length"
                  :key="activeRequest.requestId + '-' + type + '-empty'"
                  class="doc-card"
                >
                  <div class="doc-preview doc-preview--in-card">
                    <div class="doc-preview-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="5" y="2" width="14" height="20" rx="2"/>
                        <line x1="9" y1="7" x2="15" y2="7"/>
                        <line x1="9" y1="11" x2="15" y2="11"/>
                        <line x1="9" y1="15" x2="12" y2="15"/>
                      </svg>
                    </div>
                    <div class="doc-preview-body">
                      <div class="doc-preview-name">{{ docTypeShort(type) }}</div>
                      <div class="doc-preview-meta">не загружен</div>
                    </div>
                    <span class="doc-preview-status doc-status-req">Не загружен</span>
                  </div>
                </div>

                <!-- One card per uploaded file. The client can attach
                     multiple files of the same type (e.g. CERTIFICATE
                     scan + translation) and each gets its own review
                     panel. -->
                <div
                  v-for="(doc, idx) in docsByType(activeRequest, type)"
                  :key="activeRequest.requestId + '-' + type + '-' + doc.docId"
                  class="doc-card"
                >
                  <div class="doc-preview doc-preview--in-card">
                    <div class="doc-preview-icon">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="5" y="2" width="14" height="20" rx="2"/>
                        <line x1="9" y1="7" x2="15" y2="7"/>
                        <line x1="9" y1="11" x2="15" y2="11"/>
                        <line x1="9" y1="15" x2="12" y2="15"/>
                      </svg>
                    </div>
                    <div class="doc-preview-body">
                      <div class="doc-preview-name">
                        {{ docTypeShort(type) }}
                        <span
                          v-if="docsByType(activeRequest, type).length > 1"
                          class="doc-preview-index"
                        >· файл {{ idx + 1 }}</span>
                      </div>
                      <div class="doc-preview-meta">
                        {{ docSlotLabel(doc.docStatus) }}
                        <a
                          href="#"
                          class="doc-download-link"
                          @click.prevent="downloadDoc(doc)"
                        >· Скачать</a>
                      </div>
                    </div>
                    <span
                      class="doc-preview-status"
                      :class="docSlotClass(doc.docStatus)"
                    >{{ docSlotBadge(doc.docStatus) }}</span>
                  </div>

                  <!-- Decision controls per individual file. Always
                       visible while the file is PENDING so every
                       reviewer sees the full picture, but the buttons
                       are only active for the role that actually
                       owns this doc type. -->
                  <div
                    v-if="doc.docStatus === 'PENDING'"
                    class="doc-actions"
                    :class="{ 'doc-actions-locked': !canReviewDoc(doc.docType) }"
                  >
                    <div
                      v-if="!canReviewDoc(doc.docType)"
                      class="doc-actions-locked-note"
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                      Подтверждает {{ reviewerLabel(doc.docType) }}
                    </div>
                    <div class="doc-actions-buttons">
                      <button
                        class="doc-action-btn doc-action-approve"
                        :disabled="docReviewing[doc.docId] || !canReviewDoc(doc.docType)"
                        @click="approveDocClick(doc)"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                        Одобрить
                      </button>
                      <button
                        class="doc-action-btn doc-action-reject"
                        :disabled="docReviewing[doc.docId] || !canReviewDoc(doc.docType)"
                        @click="openRejectDoc(doc)"
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        Отклонить
                      </button>
                    </div>
                  </div>
                  <!-- Reviewed file: show who/when decided plus the
                       saved comment if any was left. -->
                  <div
                    v-else-if="doc.decidedAt || doc.mlroDesc"
                    class="doc-decision"
                  >
                    <div v-if="doc.decidedAt" class="doc-decision-meta">
                      <span
                        class="doc-decision-status"
                        :class="docSlotClass(doc.docStatus)"
                      >{{ doc.docStatus === 'APPROVED' ? '✓ Одобрено' : '✕ Отклонено' }}</span>
                      <span class="doc-decision-actor">{{ docDecisionActor(doc) }}</span>
                      <span class="doc-decision-when">· {{ formatDocDate(doc.decidedAt) }}</span>
                    </div>
                    <div v-if="doc.mlroDesc" class="doc-decision-comment">
                      <span class="doc-decision-tag">Клиенту:</span> {{ doc.mlroDesc }}
                    </div>
                    <div v-if="doc.internalDesc" class="doc-decision-comment doc-decision-internal">
                      <span class="doc-decision-tag">Внутренняя (не видна клиенту):</span> {{ doc.internalDesc }}
                    </div>
                  </div>
                </div>

                <!-- Предыдущие (замещённые переподачей) решения по этому типу
                     документа. Для аудита: причины прошлых отказов
                     Compliance/MLRO, включая внутреннюю причину, которая на
                     оживлённой заявке иначе не видна. -->
                <div
                  v-if="pastDecisionsByType(activeRequest, type).length"
                  :key="activeRequest.requestId + '-' + type + '-past'"
                  class="doc-past"
                >
                  <button
                    type="button"
                    class="doc-past-toggle"
                    @click="togglePastDecisions(activeRequest.requestId + '-' + type)"
                  >
                    <span class="doc-past-chev">{{ pastOpen[activeRequest.requestId + '-' + type] ? '▾' : '▸' }}</span>
                    Предыдущие решения ({{ pastDecisionsByType(activeRequest, type).length }})
                  </button>
                  <div v-show="pastOpen[activeRequest.requestId + '-' + type]" class="doc-past-list">
                    <div
                      v-for="pd in pastDecisionsByType(activeRequest, type)"
                      :key="'past-' + pd.docId"
                      class="doc-decision doc-past-item"
                    >
                      <div class="doc-decision-meta">
                        <span class="doc-decision-status" :class="docSlotClass(pd.docStatus)">{{ pd.docStatus === 'APPROVED' ? '✓ Одобрено' : '✕ Отклонено' }}</span>
                        <span class="doc-decision-actor">{{ docDecisionActor(pd) }}</span>
                        <span v-if="pd.decidedAt" class="doc-decision-when">· {{ formatDocDate(pd.decidedAt) }}</span>
                      </div>
                      <div v-if="pd.mlroDesc" class="doc-decision-comment">
                        <span class="doc-decision-tag">Клиенту:</span> {{ pd.mlroDesc }}
                      </div>
                      <div v-if="pd.internalDesc" class="doc-decision-comment doc-decision-internal">
                        <span class="doc-decision-tag">Внутренняя (не видна клиенту):</span> {{ pd.internalDesc }}
                      </div>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- Request status timeline — uses the snapshot trail from
                 /classification-requests/{id}/timeline so the reviewer
                 can see when each transition happened and who pushed it. -->
            <div class="req-modal-section full">
              <div class="req-modal-sec-title">История заявки</div>
              <div
                v-if="requestTimelinesLoading[activeRequest.requestId]"
                class="sp-empty"
              >Загрузка…</div>
              <div
                v-else-if="!(requestTimelines[activeRequest.requestId] && requestTimelines[activeRequest.requestId].length)"
                class="sp-empty"
              >Истории нет</div>
              <ol v-else class="req-timeline">
                <li
                  v-for="entry in requestTimelines[activeRequest.requestId]"
                  :key="entry.snapshotId"
                  class="req-timeline-row"
                >
                  <span class="req-timeline-dot" :class="requestStatusClass(entry.status)"></span>
                  <div class="req-timeline-body">
                    <div class="req-timeline-head">
                      <span class="req-timeline-status">{{ requestStatusLabel(entry.status) }}</span>
                      <span class="req-timeline-when">{{ formatDocDate(entry.changedAt) }}</span>
                    </div>
                    <div class="req-timeline-meta">
                      <span v-if="entry.changedByType" class="req-timeline-type">{{ actorTypeBadge(entry.changedByType) }}</span>
                      <span class="req-timeline-actor">· {{ actorLabel(entry) }}</span>
                    </div>
                  </div>
                </li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import { apiDomain, getHeader } from '@/config/config'

export default {
  name: 'UserDetail',
  props: {
    userId: { type: [String, Number], default: null },
    readOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      activeTab: 'ident',
      tabs: [
        { key: 'ident',    label: 'Идентификация' },
        { key: 'doc',      label: 'Документ' },
        { key: 'addr',     label: 'Адрес' },
        { key: 'finance',  label: 'Финансы' },
        { key: 'files',    label: 'Файлы' },
        { key: 'aml',      label: 'AML' },
        { key: 'requests', label: 'Заявки' },
        // "Квалификация" — summary tab: current classification, brief
        // exam history, and brief reclassification history. Per-attempt
        // drill-downs are reached from here (the standalone tests tab
        // was removed; the same data is reachable from "Квалификация").
        { key: 'qual',     label: 'Квалификация' },
        { key: 'actions',  label: 'Действия', right: true },
      ],

      // Classification requests ("Заявки" tab). One row per
      // reclassification attempt the user has filed. Fetched lazily
      // when the tab is first opened.
      requests: [],
      requestsLoading: false,
      requestsLoaded: false,
      // Modal state — selected request id and currently-rendered
      // payload (mockup mirrors this with reqModalBody).
      requestModalOpen: false,
      activeRequestId: null,
      // MLRO comment scratch — keyed by docId so each row keeps its
      // own typed text while siblings are untouched. Wiped after a
      // successful review.
      docComments: {},
      // "review in flight" lock per docId so the buttons can't
      // double-fire while the API call is on the wire.
      docReviewing: {},
      // Раскрытые блоки «Предыдущие решения» по ключу requestId+type.
      pastOpen: {},
      // Reject-doc modal: причина обязательна и уходит клиенту в письме.
      rejectDocFor: null,
      rejectDocReason: '',
      // Внутренняя причина отклонения (для аудита, не видна клиенту).
      rejectDocInternal: '',
      rejectDocBusy: false,
      // Approve-limit modal: при одобрении SoF-документа MLRO вводит
      // месячный лимит инвестиций вручную (применяется при финализации).
      approveDocFor: null,
      approveLimitValue: '',
      // Максимально допустимый лимит для категории заявки (из конфига).
      approveLimitMax: null,
      // Необязательный комментарий для аудита при одобрении документа.
      approveComment: '',
      approveDocBusy: false,
      // Timeline cache per requestId so reopening the modal doesn't
      // re-fetch needlessly. Cleared on successful review so the
      // newly-written row shows up.
      requestTimelines: {},
      requestTimelinesLoading: {},

      fixText: '',
      riskLevel: 0,
      showModal: false,

      amlRiskLevel: '',

      snapshots: [],
      snapshotsLoading: false,
      docs: [],
      docsLoading: false,
      docsLoaded: false,
      // AMLBot KYC-сканы (удост. личности / подтверждение адреса / селфи).
      kycFiles: [],
      kycFilesLoading: false,
      kycFilesLoaded: false,
      kycThumbs: {}, // fileId -> objectURL превью (изображения)
      docStatusOptions: [
        { value: 'PENDING',  text: 'PENDING' },
        { value: 'APPROVED', text: 'APPROVED' },
        { value: 'REJECTED', text: 'REJECTED' },
      ],
      docsSaving: {},

      attempts: [],
      attemptsLoading: false,
      attemptsLoaded: false,
      attemptDetails: {},
      attemptDetailsLoading: {},
      // Open/closed flag per attemptId for the click-to-expand
      // answer list under each row. Object so Vue 2 reactivity stays
      // happy via $set.
      attemptOpen: {},
      // Per-attempt accordion state for the "Тестирование и опросники"
      // list on the Квалификация tab. Independent of attemptOpen on
      // the dedicated "Тестирование" tab so toggling on one side
      // doesn't churn the other.
      qTestOpen: {},
      // Block / unblock modal — typed reason. Required for block events,
      // optional on unblock.
      blockReason: '',
      // Причина блокировки — выбор из двух стандартных вариантов; код
      // (INFOSEC/TERMINATION) уходит на бэкенд и определяет текст письма.
      blockReasonCode: '',
      blockReasonOptions: [
        { value: 'INFOSEC', text: 'В рамках информационной безопасности' },
        { value: 'TERMINATION', text: 'При расторжении деловых отношений' },
      ],
      // Append-only journal pulled from /exchange/userdetails/{id}/block-log
      // (p_user_block_log on the backend). Replaces the old computed
      // that scraped snapshots — now we get real reason + admin author
      // straight from the source table.
      blockLogServer: [],
      blockLogLoading: false,
      // Заморозка аккаунта — обязательный комментарий для аудита.
      freezeReason: '',
      freezeBusy: false,
      // AML risk-override form state.
      amlRiskOverride: '',
      amlRiskComment: '',
      // Текущее переопределение риска (последняя запись) + история.
      riskOverrideCurrent: null,
      riskLog: [],
      riskBusy: false,

      kycLoading: false,
      kycError: '',
      kycResults: [],

      applicantLoading: false,
      applicantError: '',
      applicantRaw: null,
      applicantSelectedVerificationId: '',

      lastApplicantLoading: false,
      lastApplicantError: '',
      lastApplicantLoaded: false,

      onboardingForm: {
        status: 'PENDING',
        comment: ''
      },
      onboardingStatusOptions: [
        { value: 'PENDING', text: 'В ожидании' },
        { value: 'UNDER_REVIEW', text: 'На рассмотрении' },
        { value: 'APPROVED', text: 'Одобрено' },
        { value: 'REJECTED', text: 'Отклонено' }
      ],

      genderOptions: [
        { value: '', text: 'Выберите' },
        { value: 'M', text: 'Мужской' },
        { value: 'F', text: 'Женский' }
      ],
      residencyOptions: [
        { value: '', text: 'Выберите' },
        { value: 'resident', text: 'Резидент' },
        { value: 'non_resident', text: 'Нерезидент' }
      ],
      businessCharacterOptions: [
        { value: '1', text: 'Разовая операция' },
        { value: '2', text: 'Регулярное сотрудничество' },
        { value: '3', text: 'Пассивное хранение активов' },
        { value: '4', text: 'Долгосрочные инвестиции' },
        { value: '5', text: 'Высокочастотные транзакции' },
        { value: '6', text: 'Иное' }
      ],

      countries: [],
      docTypes: [],

      userForm: {
        userId: 0,
        lastName: '', firstName: '', surName: '',
        birthDate: '', birthPlace: '', iin: '', citizen: '', gender: '',
        isPep: 0,
        docTypeId: '', kycNumber: '', kycSerial: '',
        kycIssuedBy: '', kycGivenDate: '', kycTillDate: '',
        residencySign: '', residencyCountry: '',
        email: '', phoneNumber: '',
        pepTagInvolvement: '', pepTagSource: '',
        pepFio: '', pepPosition: '', pepRelDeg: '',
        realCountry: '', realRegion: '', realIndex: '', realCity: '', realAddr: '',
        regCountry: '',  regRegion: '',  regIndex: '',  regCity: '',  regAddr: '',
        jobAddr: '', jobTitle: '', jobBusinessPurpose: '', jobBusiness: '',
        moneySource: '', wealthSource: '',
      },

      loadedUserData: {
        id: 0,
        email: '',
        userName: '',
        firstName: '',
        lastName: '',
        onboardingStatus: 'PENDING',
        actionComments: []
      }
    }
  },

  computed: {
    ...mapState(['loginedUser']),
    // AMLBot-сканы, сгруппированные по типу документа в осмысленном
    // порядке: удостоверение → адрес → селфи → прочее.
    kycFileGroups() {
      const order = ['GOVERNMENT_ID', 'ADDRESS_DOCUMENT', 'SELFIE_IMAGE']
      const byType = {}
      for (const f of this.kycFiles) {
        const t = f.documentType || 'OTHER'
        ;(byType[t] = byType[t] || []).push(f)
      }
      const types = Object.keys(byType).sort((a, b) => {
        const ia = order.indexOf(a), ib = order.indexOf(b)
        return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
      })
      return types.map(t => ({ type: t, files: byType[t] }))
    },
    // Set of role names the logged-in admin holds. Used to gate the
    // approve/reject buttons per doc type — MLRO handles SoF, the
    // Compliance officer handles certificates / history, ADMIN does
    // everything.
    currentRoleNames() {
      const r = (this.loginedUser && this.loginedUser.userRoles) || []
      const set = new Set()
      r.forEach(x => { if (x && x.name) set.add(String(x.name).toUpperCase()) })
      return set
    },
    canActAsAdmin() { return this.currentRoleNames.has('ADMIN') },
    resolvedUserId() {
      return this.userId || (this.$route && this.$route.params && this.$route.params.userId) || null
    },
    amlScoreLabel() {
      const isPep = this.userForm && (this.userForm.isPep === 1 || this.userForm.isPep === '1')
      if (isPep) return 'Высокий'
      // Базовый уровень для не-ПДЛ клиентов — Средний.
      return 'Средний'
    },
    riskValClass() {
      const v = this.amlScoreLabel
      if (v === 'Высокий') return 'val-high'
      if (v === 'Средний') return 'val-warn'
      return 'val-ok'
    },
    // Эффективный риск: ручное переопределение перекрывает вычисленный
    // базовый (ПДЛ → Высокий, иначе Средний).
    effectiveRisk() {
      if (this.riskOverrideCurrent && this.riskOverrideCurrent.riskLevel) {
        return this.riskLevelLabel(this.riskOverrideCurrent.riskLevel)
      }
      return this.amlScoreLabel
    },
    effectiveRiskClass() {
      const v = this.effectiveRisk
      if (v === 'Критический') return 'val-high'
      if (v === 'Высокий') return 'val-high'
      if (v === 'Средний') return 'val-warn'
      return 'val-ok'
    },
    // Переопределён ли риск вручную (для пометки в UI).
    riskIsOverridden() {
      return !!(this.riskOverrideCurrent && this.riskOverrideCurrent.riskLevel)
    },
    amlHighRiskReason() {
      const reasons = []
      if (this.userForm && (this.userForm.isPep === 1 || this.userForm.isPep === '1')) reasons.push('ПДЛ')
      return reasons.length ? reasons.join(' • ') : 'Не выявлено'
    },
    // List of AML flags shown in the AML-профиль summary. Today PDL
    // is the only source — when AMLBot screening starts persisting
    // sanctions / SNC matches we'll add them here.
    amlFlagsLabel() {
      const flags = []
      if (this.userForm && (this.userForm.isPep === 1 || this.userForm.isPep === '1')) flags.push('PEP')
      return flags.length ? flags.join(' • ') : 'нет флагов'
    },
    amlFlagsClass() {
      const isPep = this.userForm && (this.userForm.isPep === 1 || this.userForm.isPep === '1')
      return isPep ? 'fv-high' : 'fv-ok'
    },
    identificationStatusLabel() {
      const map = {
        APPROVED:      'Пройдена',
        PENDING:       'На проверке',
        REJECTED:      'Отклонена',
        NOTIDENTIFIED: 'Неидентифицирован',
      }
      const v = (this.loadedUserData && this.loadedUserData.onboardingStatus)
        || (this.userForm && this.userForm.onboardingStatus)
        || 'NOTIDENTIFIED'
      return map[v] || v
    },
    statusValClass() {
      const v = (this.loadedUserData && this.loadedUserData.onboardingStatus) || ''
      if (v === 'APPROVED') return 'val-ok'
      if (v === 'REJECTED') return 'val-high'
      if (v === 'PENDING')  return 'val-warn'
      return ''
    },
    classificationStatusLabel() {
      const map = {
        NONE:         'Не назначен',
        RETAIL_NQ:    'Розничный (неквалифицированный)',
        RETAIL_Q:     'Розничный (квалифицированный)',
        PROFESSIONAL: 'Профессиональный',
      }
      const v = (this.loadedUserData && this.loadedUserData.clientCategory)
        || (this.userForm && this.userForm.clientCategory)
        || 'NONE'
      return map[v] || v
    },
    // Optional "Деловые отношения" section on Finance — only shown
    // when at least one of the two business-relationship fields has a
    // value. Keeps the default Finance layout 1:1 with the mock.
    hasBusinessFinance() {
      const business = this.userForm && this.userForm.jobBusiness
      const purpose  = this.userForm && this.userForm.jobBusinessPurpose
      return !!(business && String(business).trim()) || !!(purpose && String(purpose).trim())
    },
    // ── Actions tab logs (derived from user snapshots) ──────────────
    // 2FA reset journal — every snapshot row where totp_enabled went
    // from 1 → 0 / NULL (admin reset wipes the secret). Older rows last.
    twoFaResetLog() {
      const list = this.snapshots || []
      const out = []
      // Snapshots come newest-first from the API; iterate oldest-first
      // so each pair (prev, curr) reflects a real transition.
      const chrono = [...list].reverse()
      for (let i = 1; i < chrono.length; i++) {
        const prev = chrono[i - 1]
        const curr = chrono[i]
        const prevOn = prev && (prev.totpEnabled === 1 || prev.totpEnabled === true)
        const currOn = curr && (curr.totpEnabled === 1 || curr.totpEnabled === true)
        if (prevOn && !currOn) {
          out.push({
            at: curr.changedAt,
            by: curr.changedByName || curr.changedByEmail || this.qualActorLabel(curr.changedByType),
            comment: '',
          })
        }
      }
      return out.reverse()
    },
    // Block / Unblock journal — now fed from /block-log endpoint
    // (p_user_block_log on the backend). Append-only, the row's
    // `action` column drives the badge colour ("block" → red,
    // "unblock" → green); `by_name` / `by_email` resolves to a real
    // admin name; `reason` is the typed-at-the-moment comment.
    blockLog() {
      return (this.blockLogServer || []).map(entry => ({
        type: entry.action,
        at: entry.createdAt,
        by: entry.byName || entry.byEmail || (entry.byUserId ? ('ID ' + entry.byUserId) : 'Система'),
        reason: entry.reason || '',
      }))
    },
    // Latin-script names — currently not stored on the backend (AMLBot
    // delivers both origin_* (cyr) and bare first_name/last_name (latin)
    // but we only persist the cyrillic version). Render the labels for
    // the mock anyway; values stay "не заполнено" until a latin column
    // lands. These computeds are the seams to swap in when it does.
    latinFirstName() {
      return (this.loadedUserData && this.loadedUserData.firstNameLatin) || ''
    },
    latinLastName() {
      return (this.loadedUserData && this.loadedUserData.lastNameLatin) || ''
    },
    latinThirdName() {
      return (this.loadedUserData && this.loadedUserData.thirdNameLatin) || ''
    },
    // Risk-level proxy until a real risk-scoring column exists on
    // lms_core_user: PEP=1 → Высокий, иначе Средний (базовый уровень).
    identRiskLevel() {
      const isPep = this.userForm && this.userForm.isPep
      if (isPep === 1 || isPep === '1') return 'Высокий'
      if (isPep === 0 || isPep === '0') return 'Средний'
      return ''
    },
    // "Результат тестирования" — самый свежий verdict-тест с известным
    // score (BASIC / QUALIFIED / PROFESSIONAL). Surveys (ONBOARDING) не
    // pass/fail так что в эту строку не попадают.
    identTestResult() {
      const list = this.attempts || []
      const ranked = list.filter(a =>
        a && a.score != null && this.testCodeHasVerdict(a.testCode)
      )
      if (!ranked.length) return ''
      const fresh = ranked[0]
      return fresh.score + '/' + fresh.totalQuestionCount
    },
    // Step-by-step ladder of "когда впервые попал в категорию X" —
    // sourced from /userdetails categoryHistory (snapshot-derived).
    // Returns only tiers the user has actually been in, so brand-new
    // accounts collapse the section cleanly.
    qualCategoryTimeline() {
      const h = (this.loadedUserData && this.loadedUserData.categoryHistory) || {}
      const order = [
        { code: 'RETAIL_NQ',    key: 'retailNQ',     label: 'Розничный (неквалифицированный)', tone: 'neutral' },
        { code: 'RETAIL_Q',     key: 'retailQ',      label: 'Розничный (квалифицированный)',   tone: 'success' },
        { code: 'PROFESSIONAL', key: 'professional', label: 'Профессиональный',                 tone: 'primary' },
      ]
      return order
        .filter(s => h[s.key])
        .map(s => ({ ...s, at: h[s.key].at, by: h[s.key].by }))
    },
    // Active classification requests — PENDING / DRAFT. Newest first
    // so the user sees the most recent attempt at the top of the list.
    activeRequests() {
      return (this.requests || [])
        .filter(request => {
          const status = (request.status || '').toUpperCase()
          return status === 'PENDING' || status === 'DRAFT'
        })
        .sort((a, b) => (b.requestId || 0) - (a.requestId || 0))
    },
    // Reverse-chronological copy of `requests` for the recls history
    // section (mock renders `reclsHistory.slice().reverse()`).
    qualReclsList() {
      return [...(this.requests || [])].slice().sort((a, b) => {
        const ta = new Date(a.createdAt || 0).getTime()
        const tb = new Date(b.createdAt || 0).getTime()
        return tb - ta
      })
    },
    // "Когда присвоена текущая классификация" — primary source is
    // categoryHistory (snapshot-derived MIN(changed_at) per tier), so
    // даже RETAIL_NQ (где нет соответствующего classification request)
    // имеет дату. Fallback на decidedAt последней APPROVED-заявки —
    // legacy path для аккаунтов с ручным переводом до того, как мы
    // начали писать snapshot'ы.
    qualAssignedAt() {
      const cat = (this.loadedUserData && this.loadedUserData.clientCategory) || ''
      const h = (this.loadedUserData && this.loadedUserData.categoryHistory) || {}
      const key = ({
        RETAIL_NQ:    'retailNQ',
        RETAIL_Q:     'retailQ',
        PROFESSIONAL: 'professional',
      })[cat]
      if (key && h[key] && h[key].at) return h[key].at
      const r = (this.requests || []).find(x => (x.status || '').toUpperCase() === 'APPROVED')
      if (r) return r.decidedAt || r.updatedAt || r.createdAt || ''
      return ''
    },
    // "На каком основании" присвоена. Sources combine:
    //   1. APPROVED reclassification request (если есть)
    //   2. для RETAIL_NQ — passed BASIC test (онбординг)
    //   3. иначе текстовый fallback "перевод <by>" из categoryHistory
    qualBasis() {
      const r = (this.requests || []).find(x => (x.status || '').toUpperCase() === 'APPROVED')
      if (r) {
        const parts = []
        if (r.attemptId)         parts.push('тест пройден')
        if (r.hasCertificateDoc) parts.push('сертификат подтверждён')
        if (r.hasSofDoc)         parts.push('SoF подтверждён')
        if (!parts.length && r.fromCategory && r.requestedCategory) {
          parts.push(r.fromCategory + ' → ' + r.requestedCategory)
        }
        if (parts.length) return parts.join(', ')
      }
      const cat = (this.loadedUserData && this.loadedUserData.clientCategory) || ''
      if (cat === 'RETAIL_NQ') return 'базовый тест пройден, KYC завершён'
      const h = (this.loadedUserData && this.loadedUserData.categoryHistory) || {}
      const key = ({ RETAIL_NQ: 'retailNQ', RETAIL_Q: 'retailQ', PROFESSIONAL: 'professional' })[cat]
      if (key && h[key] && h[key].by) return 'перевод: ' + this.qualActorLabel(h[key].by)
      return ''
    },
    // Short form of the classification label so it fits the 4-cell
    // status bar without wrapping (the long version stays on the
    // "Статус и классификация" section card).
    shortClassificationLabel() {
      const map = {
        NONE:         'Не назначен',
        RETAIL_NQ:    'Розничный неквалиф.',
        RETAIL_Q:     'Розничный квалиф.',
        PROFESSIONAL: 'Профессиональный',
      }
      const v = (this.loadedUserData && this.loadedUserData.clientCategory) || 'NONE'
      return map[v] || v
    },
    headerEmail() {
      return (this.loadedUserData && this.loadedUserData.email)
        || (this.userForm && this.userForm.email)
        || ''
    },
    headerDisplayName() {
      const d = this.loadedUserData || {}
      const f = this.userForm || {}
      const fio = [
        d.lastName  || f.lastName,
        d.firstName || f.firstName,
        d.thirdName || f.surName,
      ].filter(Boolean).join(' ').trim()
      if (fio) return fio
      const email = this.headerEmail
      if (email) return email
      const id = d.id || f.userId || this.resolvedUserId
      return id ? ('Пользователь #' + id) : 'Пользователь'
    },
    activeRequest() {
      if (!this.activeRequestId) return null
      return (this.requests || []).find(r => r.requestId === this.activeRequestId) || null
    },
    // Ручной лимит превышает максимум категории (из конфига).
    approveLimitOverMax() {
      if (this.approveLimitMax == null) return false
      const v = Number(this.approveLimitValue)
      return Number.isFinite(v) && v > Number(this.approveLimitMax)
    },
    // True while the account is unblocked. Backend exposes is_active
    // as 1/0, sometimes as a string — coerce defensively.
    isUserActive() {
      const v = this.loadedUserData && this.loadedUserData.isActive
      if (v === undefined || v === null) return true
      return v === 1 || v === '1' || v === true
    },
    // Заморожен ли аккаунт (account_frozen). Отдельно от isActive.
    isAccountFrozen() {
      const v = this.loadedUserData && this.loadedUserData.accountFrozen
      return v === 1 || v === '1' || v === true
    },
    // Tab order is dynamic: "Заявки" leads only when the user has an
    // ACTIVE (PENDING/DRAFT) request needing attention, and is hidden
    // otherwise. Closed requests live in the "Квалификация" history, so
    // the tab stays clean when there's nothing to act on.
    visibleTabs() {
      if (this.activeRequests && this.activeRequests.length) {
        const requests = this.tabs.find(t => t.key === 'requests')
        const rest = this.tabs.filter(t => t.key !== 'requests')
        return requests ? [requests, ...rest] : rest
      }
      return this.tabs.filter(t => t.key !== 'requests')
    },
    headerInitials() {
      const d = this.loadedUserData || {}
      const f = this.userForm || {}
      const last  = (d.lastName  || f.lastName  || '').trim()
      const first = (d.firstName || f.firstName || '').trim()
      const pick = (s) => (s && s[0]) ? s[0] : ''
      let init = (pick(last) + pick(first)).toUpperCase()
      if (init) return init
      const email = this.headerEmail || ''
      const e = email.replace(/[^A-Za-zА-Яа-яЁё0-9]/g, '').trim()
      init = e.slice(0, 2).toUpperCase()
      return init || 'ПЛ'
    },
  },

  methods: {
    // ---- Account actions (block / unblock / 2FA reset) ----
    openBlockModal() {
      this.blockReason = ''
      this.blockReasonCode = ''
      this.$bvModal && this.$bvModal.show('block-user-modal-detail')
    },
    performToggleActive() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      // /user/changestatus toggles is_active server-side (UserModel.
      // ChnageUserStatus reads current value and flips it), so the
      // backend doesn't need a desired state — it derives it. Комментарий
      // обязателен и для блокировки, и для разблокировки — идёт в
      // append-only журнал блокировок.
      let reason = ''
      let reasonCode = ''
      if (this.isUserActive) {
        // Блокировка: причина обязательна — из выпадающего списка. Код
        // определяет текст письма; в журнал пишем читаемую метку + опц. коммент.
        reasonCode = this.blockReasonCode
        const note = (this.blockReason || '').trim()
        // Причина (из списка) и комментарий — оба обязательны.
        if (!reasonCode || !note) return
        const opt = this.blockReasonOptions.find(o => o.value === reasonCode)
        const label = (opt && opt.text) || reasonCode
        reason = label + ' — ' + note
      } else {
        // Разблокировка: свободная причина, обязательна.
        reason = (this.blockReason || '').trim()
        if (!reason) return
      }
      axios.post(
        `${apiDomain}/user/changestatus`,
        { userId: Number(userId), reason, reasonCode },
        { headers: getHeader() },
      )
        .then(() => {
          const next = !this.isUserActive
          this.$bvToast && this.$bvToast.toast(
            next ? 'Пользователь разблокирован.' : 'Пользователь заблокирован.',
            { title: 'Готово', variant: 'success', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 2500 }
          )
          this.blockReason = ''
          this.blockReasonCode = ''
          this.loadUserData(userId)
          this.fetchBlockLog()
        })
        .catch(err => {
          const sc = err && err.response && err.response.status
          let msg = 'Не удалось изменить статус.'
          if (sc === 401 || sc === 403) msg = 'Недостаточно прав.'
          this.$bvToast && this.$bvToast.toast(msg, {
            title: 'Ошибка', variant: 'danger',
            toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4500,
          })
          console.error('changestatus error:', err)
        })
    },
    // ---- Заморозка / разморозка аккаунта ----
    openFreezeModal() {
      this.freezeReason = ''
      this.$bvModal && this.$bvModal.show('freeze-user-modal')
    },
    performToggleFreeze() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      const reason = (this.freezeReason || '').trim()
      if (!reason) return // комментарий для аудита обязателен
      const frozen = !this.isAccountFrozen // целевое состояние
      this.freezeBusy = true
      axios.post(
        `${apiDomain}/user/freeze-status`,
        { userId: Number(userId), frozen, reason },
        { headers: getHeader() },
      )
        .then(() => {
          this.$bvToast && this.$bvToast.toast(
            frozen ? 'Аккаунт заморожен. Клиент уведомлён.' : 'Заморозка снята. Клиент уведомлён.',
            { title: 'Готово', variant: 'success', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 2500 }
          )
          this.freezeReason = ''
          this.$bvModal && this.$bvModal.hide('freeze-user-modal')
          this.loadUserData(userId)
          this.fetchBlockLog()
        })
        .catch(err => {
          const sc = err && err.response && err.response.status
          let msg = 'Не удалось изменить состояние заморозки.'
          if (sc === 401 || sc === 403) msg = 'Недостаточно прав.'
          this.$bvToast && this.$bvToast.toast(msg, {
            title: 'Ошибка', variant: 'danger',
            toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4500,
          })
          console.error('freeze-status error:', err)
        })
        .finally(() => { this.freezeBusy = false })
    },
    // Журнал блокировок/заморозок: метка и цвет по типу события.
    blogLabel(type) {
      return ({ block: 'Заблокирован', unblock: 'Разблокирован', freeze: 'Заморожен', unfreeze: 'Разморожен' })[type] || type
    },
    blogColor(type) {
      return ({ block: '#EF4444', unblock: '#10B981', freeze: '#F59E0B', unfreeze: '#10B981' })[type] || '#6B7280'
    },
    // AML risk-override submit — UI-only stub. Once the backend
    // exposes an endpoint (e.g. POST /exchange/userdetails/{id}/risk),
    // wire the POST here and pull the updated value back into
    // loadedUserData. For now we just acknowledge the click so the
    // analyst sees feedback.
    // Текущее переопределение риска + история (вкладка AML).
    fetchRisk() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      axios.get(`${apiDomain}/exchange/userdetails/${userId}/risk`, { headers: getHeader() })
        .then(res => { this.riskOverrideCurrent = res.data || null })
        .catch(() => { this.riskOverrideCurrent = null })
      axios.get(`${apiDomain}/exchange/userdetails/${userId}/risk-log`, { headers: getHeader() })
        .then(res => { this.riskLog = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.riskLog = [] })
    },
    riskLevelLabel(level) {
      return ({ MEDIUM: 'Средний', HIGH: 'Высокий', CRITICAL: 'Критический' })[level] || level
    },
    riskLevelClass(level) {
      if (level === 'CRITICAL' || level === 'HIGH') return 'val-high'
      if (level === 'MEDIUM') return 'val-warn'
      return 'val-ok'
    },
    // Ручное переопределение риска — POST на бэкенд (обоснование
    // обязательно), затем перечитываем текущее значение и историю.
    saveAmlRiskOverride() {
      if (!this.amlRiskOverride || !this.amlRiskComment.trim() || this.riskBusy) return
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      this.riskBusy = true
      axios.post(
        `${apiDomain}/exchange/userdetails/${userId}/risk`,
        { riskLevel: this.amlRiskOverride, reason: this.amlRiskComment.trim() },
        { headers: getHeader() },
      )
        .then(() => {
          this.$bvToast && this.$bvToast.toast('Риск клиента обновлён.', {
            title: 'Готово', variant: 'success', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 3000,
          })
          this.amlRiskOverride = ''
          this.amlRiskComment = ''
          this.fetchRisk()
        })
        .catch((err) => {
          const body = err && err.response && err.response.data
          const txt = typeof body === 'string' ? body : ''
          let msg = 'Не удалось сохранить изменение риска.'
          if (txt.indexOf('reason-required') !== -1) msg = 'Укажите обоснование изменения.'
          this.$bvToast && this.$bvToast.toast(msg, {
            title: 'Ошибка', variant: 'danger', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4000,
          })
        })
        .finally(() => { this.riskBusy = false })
    },
    // Pull the append-only block-log for the Actions tab. Cached on
    // first fetch; performToggleActive re-pulls after a successful flip
    // so the journal reflects the brand-new event.
    fetchBlockLog() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      this.blockLogLoading = true
      axios.get(`${apiDomain}/exchange/userdetails/${userId}/block-log`, { headers: getHeader() })
        .then(res => { this.blockLogServer = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.blockLogServer = [] })
        .finally(() => { this.blockLogLoading = false })
    },
    openResetTwoFAModal() {
      this.$bvModal && this.$bvModal.show('reset-2fa-modal')
    },
    performResetTwoFA() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      axios.post(`${apiDomain}/api/2fa/admin-reset/${userId}`, {}, { headers: getHeader() })
        .then(() => {
          this.$bvToast && this.$bvToast.toast(
            '2FA пользователя сброшена. При следующем входе он пройдёт настройку заново.',
            { title: 'Готово', variant: 'success', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 3500 }
          )
        })
        .catch(err => {
          const sc = err && err.response && err.response.status
          let msg = 'Не удалось сбросить 2FA.'
          if (sc === 401 || sc === 403) msg = 'Недостаточно прав для сброса 2FA.'
          this.$bvToast && this.$bvToast.toast(msg, {
            title: 'Ошибка', variant: 'danger',
            toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4500,
          })
        })
    },
    setTab(t) {
      if (!t) return
      this.activeTab = t.key
      if (t.key === 'doc' && !this.kycFilesLoaded && !this.kycFilesLoading) this.loadKycFiles()
      if (t.key === 'files')    this.onDocsTabClicked()
      if (t.key === 'tests')    this.onAttemptsTabClicked()
      if (t.key === 'requests') this.onRequestsTabClicked()
      if (t.key === 'actions') {
        this.loadSnapshots()
        this.fetchBlockLog()
      }
      if (t.key === 'aml') this.fetchRisk()
      // Quals reuses the same two datasets (attempts + requests) but
      // pulls them in parallel on first open so the summary cards
      // render without the user having to visit each source tab first.
      if (t.key === 'qual') {
        this.onAttemptsTabClicked()
        this.onRequestsTabClicked()
      }
    },
    emptyClass(v) {
      if (v == null) return 'empty'
      const s = String(v).trim()
      return s === '' ? 'empty' : ''
    },
    genderLabel(g) {
      if (g === 'M' || g === 'm') return 'Мужской'
      if (g === 'F' || g === 'f') return 'Женский'
      return 'не заполнено'
    },
    pepLabel(v) {
      if (v === 1 || v === '1') return 'Да'
      if (v === 0 || v === '0') return 'Нет'
      return '—'
    },
    countryLabel(code) {
      if (!code) return ''
      const c = (this.countries || []).find(c => c && c.code === code)
      return c ? (c.nameRu || c.nameEn || code) : code
    },
    businessLabel(v) {
      if (!v) return 'не заполнено'
      const o = this.businessCharacterOptions.find(x => x.value == v) // eslint-disable-line
      return o ? o.text : v
    },
    docStatusClass(s) {
      const v = String(s || '').toUpperCase()
      if (v === 'APPROVED') return 'doc-status-ok'
      if (v === 'REJECTED') return 'doc-status-bad'
      if (v === 'PENDING')  return 'doc-status-req'
      return 'doc-status-req'
    },
    // Human label for the doc-status pill — backend stores APPROVED /
    // REJECTED / PENDING (uppercase enum), the chip shows it in Russian.
    docStatusLabel(s) {
      const v = String(s || '').toUpperCase()
      if (v === 'APPROVED') return '✓ Загружен'
      if (v === 'REJECTED') return '✕ Отклонён'
      if (v === 'PENDING')  return 'На проверке'
      return '—'
    },
    // Unified verdict — tests and surveys read the same way: "Пройден"
    // for any completed attempt, "Не пройден" only for tests we know
    // failed (a.passed === false). Surveys never report passed=false
    // (they're not pass/fail), so they always land on "Пройден".
    attemptVerdictLabel(a) {
      if (!a) return '—'
      if (a.passed === false) return 'Не пройден'
      return 'Пройден'
    },
    attemptVerdictClass(a) {
      if (!a) return ''
      if (a.passed === false) return 'doc-status-bad'
      return 'doc-status-ok'
    },

    // ============ Existing methods kept as-is ============
    onOboardingStatusChange() {
      const current = this.loadedUserData.onboardingStatus || 'PENDING'
      this.onboardingForm.status = current
      this.onboardingForm.comment = ''
      this.kycError = ''
      this.kycLoading = false
      this.$bvModal && this.$bvModal.show('onboarding-status-modal')
    },

    openApplicantRaw(r) {
      const verificationId = r && r.verification_id ? String(r.verification_id) : ''
      if (!verificationId) return
      this.applicantSelectedVerificationId = verificationId
      this.applicantError = ''
      this.applicantRaw = null
      this.applicantLoading = true
      axios
        .get(apiDomain + `/exchange/kyc/applicantraw/${verificationId}`, { headers: getHeader() })
        .then(res => {
          this.applicantRaw = res.data || null
          if (!this.applicantRaw || !this.applicantRaw.applicant_id) {
            this.applicantError = 'Данные заявителя пустые или неверного формата.'
          }
        })
        .catch(err => {
          console.error('Applicant raw error:', err)
          this.applicantError = 'Не удалось получить данные заявителя.'
        })
        .finally(() => { this.applicantLoading = false })
    },

    loadUserData(userId) {
      if (!userId) return
      axios
        .get(apiDomain + `/exchange/userdetails/${userId}`, { headers: getHeader() })
        .then(response => {
          this.loadedUserData = response.data || this.loadedUserData
          if (!this.loadedUserData.onboardingStatus) this.loadedUserData.onboardingStatus = 'PENDING'
          if (!Array.isArray(this.loadedUserData.actionComments)) this.loadedUserData.actionComments = []
          this.prefillUserFormFromLoaded()
        })
        .catch(error => {
          console.error('There was an error fetching the user data:', error)
        })
    },

    prefillUserFormFromLoaded() {
      const u = this.loadedUserData || {}
      const set = (k, v) => this.$set(this.userForm, k, v == null ? '' : v)
      set('userId',           u.id || this.userForm.userId || 0)
      set('lastName',         u.lastName)
      set('firstName',        u.firstName)
      set('surName',          u.thirdName)
      set('email',            u.email)
      set('phoneNumber',      u.phoneNumber)
      set('iin',              u.iin)
      set('citizen',          u.nationality)
      set('gender',           u.gender)
      set('birthDate',        u.dob)
      set('birthPlace',       u.birthPlace)
      const d = u.document || {}
      set('kycNumber',        d.documentNumber)
      set('kycSerial',        d.documentSeries)
      set('kycIssuedBy',      d.issuedBy)
      set('kycGivenDate',     d.issueDate)
      set('kycTillDate',      d.expiryDate)
      set('docTypeId',        d.documentType)
      set('residencyCountry', u.residenceCountry)
      set('regCountry',       u.regCountry)
      set('regRegion',        u.regRegion)
      set('regIndex',         u.regIndex)
      set('regCity',          u.regCity)
      set('regAddr',          u.regAddr)
      set('realCountry',      u.realCountry)
      set('realRegion',       u.realRegion)
      set('realIndex',        u.realIndex)
      set('realCity',         u.realCity)
      set('realAddr',         u.realAddr)
      if (u.isPep === 0 || u.isPep === 1) this.$set(this.userForm, 'isPep', u.isPep)
      set('pepTagInvolvement', u.pepInvolvement)
      set('pepTagSource',      u.pepSource)
      set('pepFio',            u.pepFio)
      set('pepPosition',       u.pepPosition)
      set('pepRelDeg',         u.pepRelDeg)
      set('jobAddr',           u.workplace)
      set('jobTitle',          u.jobTitle)
      set('jobBusinessPurpose', u.businessPurpose)
      set('jobBusiness',        u.businessKind)
      set('moneySource',       u.moneySource)
      set('wealthSource',      u.wealthSource)
    },

    listDocTypes() {
      axios.get(apiDomain + '/exchange/doc/list', { headers: getHeader() })
        .then(r => { this.docTypes = r.data })
        .catch(e => { console.error('doc types:', e) })
    },
    listCountries() {
      axios.get(apiDomain + '/exchange/country/list', { headers: getHeader() })
        .then(r => { this.countries = r.data })
        .catch(e => { console.error('countries:', e) })
    },
    loadSnapshots() {
      const userId = this.resolvedUserId
      if (!userId) return
      this.snapshotsLoading = true
      axios
        .get(apiDomain + '/exchange/userdetails/' + userId + '/snapshots', { headers: getHeader() })
        .then(res => { this.snapshots = Array.isArray(res.data) ? res.data : [] })
        .catch(() => { this.snapshots = [] })
        .finally(() => { this.snapshotsLoading = false })
    },
    formatSnapshotDate(iso) {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        if (Number.isNaN(d.getTime())) return iso
        const pad = n => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
      } catch (e) { return iso }
    },

    onDocsTabClicked() {
      if (!this.docsLoaded && !this.docsLoading) this.loadDocs()
    },
    loadDocs() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      this.docsLoading = true
      axios
        .get(`${apiDomain}/exchange/userdetails/${userId}/docs`, { headers: getHeader() })
        .then(res => { this.docs = Array.isArray(res.data) ? res.data : []; this.docsLoaded = true })
        .catch(err => { console.error('docs load error:', err); this.docs = [] })
        .finally(() => { this.docsLoading = false })
    },
    // ---- AMLBot KYC-документы (сканы) --------------------------------
    loadKycFiles() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      this.kycFilesLoading = true
      axios
        .get(`${apiDomain}/exchange/userdetails/${userId}/kyc-files`, { headers: getHeader() })
        .then(res => {
          this.kycFiles = Array.isArray(res.data) ? res.data : []
          this.kycFilesLoaded = true
          // Подгружаем превью для изображений (файлы под JWT, поэтому
          // тянем blob с заголовком и делаем objectURL).
          this.kycFiles.forEach(f => { if (this.isKycImage(f)) this.loadKycThumb(f) })
        })
        .catch(err => { console.error('kyc-files load error:', err); this.kycFiles = [] })
        .finally(() => { this.kycFilesLoading = false })
    },
    fetchKycBlob(f) {
      return axios.get(`${apiDomain}/exchange/kyc/document-file/${f.fileId}`, {
        headers: getHeader(), responseType: 'blob',
      })
    },
    loadKycThumb(f) {
      this.fetchKycBlob(f)
        .then(res => { this.$set(this.kycThumbs, f.fileId, URL.createObjectURL(res.data)) })
        .catch(() => { /* превью не критично — оставим плейсхолдер */ })
    },
    // Клик по файлу: изображение с готовым превью открываем сразу,
    // остальное (pdf/видео) тянем blob-ом и открываем в новой вкладке.
    viewKycFile(f) {
      const existing = this.kycThumbs[f.fileId]
      if (existing) { window.open(existing, '_blank', 'noopener'); return }
      this.fetchKycBlob(f)
        .then(res => {
          const url = URL.createObjectURL(res.data)
          window.open(url, '_blank', 'noopener')
        })
        .catch(err => {
          console.error('kyc file open error:', err)
          this.$bvToast && this.$bvToast.toast('Не удалось открыть документ.', {
            title: 'Ошибка', variant: 'danger', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 4000,
          })
        })
    },
    isKycImage(f) {
      return typeof f.mimeType === 'string' && f.mimeType.indexOf('image/') === 0
    },
    kycFileExt(f) {
      const m = (f.mimeType || '').toLowerCase()
      if (m.indexOf('pdf') !== -1) return 'PDF'
      if (m.indexOf('video') !== -1) return 'VIDEO'
      if (m.indexOf('image') !== -1) return 'IMG'
      return 'FILE'
    },
    kycDocTypeLabel(t) {
      const map = {
        GOVERNMENT_ID:    'Удостоверение личности',
        ADDRESS_DOCUMENT: 'Подтверждение адреса',
        SELFIE_IMAGE:     'Селфи / liveness',
        TAX_ID_NUMBER:    'Налоговый номер',
      }
      return map[t] || t || 'Документ'
    },
    kycSideLabel(s) {
      const map = {
        front: 'Лицевая', back: 'Оборот', portrait: 'Портрет',
        other_1: 'Доп. 1', other_2: 'Доп. 2', other_3: 'Доп. 3',
      }
      return map[s] || s || '—'
    },
    docTypeLabel(t) {
      const map = {
        QUALIFIED:            'Подтверждение источника средств',
        CERTIFICATE:          'Сертификат CFA / FRM / CAIA / CIIA',
        PROFESSIONAL_HISTORY: 'Торговая история',
        PROFESSIONAL_SOF:     'Подтверждение источника средств',
        PROFESSIONAL_CERT:    'Сертификат / диплом',
        PROFESSIONAL:         'Архив документов',
      }
      return map[t] || t || '—'
    },
    docTrack(t) {
      if (t === 'QUALIFIED' || t === 'CERTIFICATE') return { code: 'quals', label: 'Retail Qualified' }
      if (t === 'PROFESSIONAL' || t === 'PROFESSIONAL_HISTORY' || t === 'PROFESSIONAL_SOF' || t === 'PROFESSIONAL_CERT') return { code: 'pro', label: 'Professional' }
      return { code: 'other', label: '—' }
    },
    formatDocDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return '—'
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`
    },
    docFileName(path) {
      if (!path) return ''
      const parts = String(path).split(/[\\/]/)
      return parts[parts.length - 1] || ''
    },
    downloadDoc(doc) {
      const fileName = this.docFileName(doc && doc.docPath)
      if (!fileName) return
      const url = `${apiDomain}/ptest/downloadclientfile/${encodeURIComponent(fileName)}`
      axios.get(url, { headers: getHeader(), responseType: 'blob' })
        .then(response => {
          const blob = new Blob([response.data], { type: response.data.type || 'application/octet-stream' })
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(blob)
          link.download = fileName
          link.click()
          window.URL.revokeObjectURL(link.href)
        })
        .catch(err => {
          console.error('download failed:', err)
          if (this.$bvToast) {
            this.$bvToast.toast('Не удалось скачать файл.', {
              variant: 'danger', title: 'Ошибка', solid: true,
              toaster: 'b-toaster-top-center', autoHideDelay: 4000,
            })
          }
        })
    },

    // ---- Classification requests ----------------------------------
    onRequestsTabClicked() {
      if (this.requestsLoaded || this.requestsLoading) return
      this.loadRequests()
    },
    loadRequests() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      this.requestsLoading = true
      axios.get(`${apiDomain}/exchange/userdetails/${userId}/classification-requests`, { headers: getHeader() })
        .then(res => {
          this.requests = Array.isArray(res.data) ? res.data : []
          this.requestsLoaded = true
        })
        .catch(err => { console.error('classification-requests load:', err); this.requests = [] })
        .finally(() => { this.requestsLoading = false })
    },
    requestCategoryLabel(c) {
      const map = {
        NONE:         'Без классификации',
        RETAIL_NQ:    'Розничный (неквалиф.)',
        RETAIL_Q:     'Розничный (квалиф.)',
        PROFESSIONAL: 'Профессиональный',
      }
      return map[c] || c || '—'
    },
    requestStatusLabel(s) {
      const map = {
        DRAFT:    'Черновик',
        PENDING:  'На рассмотрении',
        APPROVED: 'Одобрена',
        REJECTED: 'Отклонена',
      }
      return map[s] || s || '—'
    },
    requestStatusClass(s) {
      switch (s) {
        case 'APPROVED': return 'req-pill-ok'
        case 'REJECTED': return 'req-pill-bad'
        case 'DRAFT':    return 'req-pill-muted'
        default:         return 'req-pill-warn'
      }
    },
    // Hard requirements per requested_category — must mirror the
    // backend's requiredDocTypes in classification_request.go so the
    // progress checklist tells the same story.
    // Bucket required + uploaded docs into named groups for the request
    // modal: "Источник средств" (always), "Сертификат" (if uploaded or
    // mandatory), "История торгов" (PRO only). Each group renders as a
    // separate .req-modal-section.full so each block of documents gets
    // its own header — mirrors how the underwriter actually evaluates
    // the application (по группам, не плоским списком).
    // Нужно ли решение Комплаенс-офицера по заявке. Если клиент прошёл
    // путь через тест (attemptId) и не приложил сертификат/историю, то
    // compliance-группы нет — подтверждение комплаенса не требуется.
    requestNeedsCompliance(request) {
      return this.requestDocumentGroups(request).some(g => g.reviewerTone === 'compliance')
    },
    requestDocumentGroups(request) {
      if (!request) return []
      const groups = []
      // Each group carries the docTypes that belong here AND the role
      // that owns the review decision — surfaces as a coloured chip
      // next to the section title. MLRO ревьюит SoF, Комплаенс-офицер
      // ревьюит сертификаты и историю торгов (same split as canReviewDocType).
      const MLRO       = { reviewerLabel: 'MLRO', reviewerTone: 'mlro' }
      const COMPLIANCE = { reviewerLabel: 'Комплаенс-офицер', reviewerTone: 'compliance' }
      const pushGroup = (label, docTypes, reviewer) => {
        if (docTypes.length) groups.push({ label, docTypes, ...reviewer })
      }
      if (request.requestedCategory === 'RETAIL_Q') {
        pushGroup('Источник средств', ['QUALIFIED'], MLRO)
        const certTypes = ['CERTIFICATE']
        if (this.docByType(request, 'CERTIFICATE')) {
          pushGroup('Сертификат', certTypes, COMPLIANCE)
        } else if (!request.attemptId) {
          // No certificate uploaded AND no passed test → surface the
          // empty certificate card so the reviewer immediately sees
          // that the qualifying step is missing.
          pushGroup('Сертификат', certTypes, COMPLIANCE)
        }
        return groups
      }
      if (request.requestedCategory === 'PROFESSIONAL') {
        pushGroup('Источник средств', ['PROFESSIONAL_SOF'], MLRO)
        if (this.docByType(request, 'PROFESSIONAL_CERT')) {
          pushGroup('Сертификат', ['PROFESSIONAL_CERT'], COMPLIANCE)
        }
        if (this.docByType(request, 'PROFESSIONAL_HISTORY')) {
          pushGroup('История торгов', ['PROFESSIONAL_HISTORY'], COMPLIANCE)
        }
        return groups
      }
      return groups
    },
    // Human label for "на каком основании подана заявка" — sketch of
    // what the regulator marked as the qualifying path:
    //   • тест пройден → "пройденный тест"
    //   • есть сертификат → "сертификат"
    //   • плюс приложен SoF → добавляем
    //   • ничего из перечисленного → "—"
    requestBasisLabel(request) {
      if (!request) return '—'
      const parts = []
      if (request.attemptId) parts.push('пройденный тест')
      if (request.requestedCategory === 'RETAIL_Q' && this.docByType(request, 'CERTIFICATE')) {
        parts.push('сертификат')
      }
      if (request.requestedCategory === 'PROFESSIONAL' && this.docByType(request, 'PROFESSIONAL_CERT')) {
        parts.push('сертификат')
      }
      if (parts.length === 0) return '—'
      return parts.join(' + ')
    },
    requiredDocsForRequest(r) {
      if (!r) return []
      if (r.requestedCategory === 'RETAIL_Q') {
        // SoF is always required; CERTIFICATE only matters as an
        // alternative to the QUALIFIED test. Show it next to SoF when
        // the user actually uploaded one (otherwise it would look
        // like a missing required doc when it isn't).
        const types = ['QUALIFIED']
        if (this.docByType(r, 'CERTIFICATE')) types.push('CERTIFICATE')
        return types
      }
      if (r.requestedCategory === 'PROFESSIONAL') {
        // Only SoF is mandatory; CERT and HISTORY are optional but,
        // if the client did upload one, it has to clear review too.
        // Surface optionals only when they exist so the modal stays
        // clean for the SoF-only path.
        const types = ['PROFESSIONAL_SOF']
        if (this.docByType(r, 'PROFESSIONAL_CERT')) types.push('PROFESSIONAL_CERT')
        if (this.docByType(r, 'PROFESSIONAL_HISTORY')) types.push('PROFESSIONAL_HISTORY')
        return types
      }
      return []
    },
    // Per required doc type: PENDING / APPROVED / REJECTED / "не загружен".
    // Picks the most decisive duplicate (REJECTED > APPROVED > PENDING),
    // same precedence the backend RecomputeStatus uses.
    docSlotStatus(r, type) {
      const docs = (r && r.documents) || []
      const matches = docs.filter(d => d.docType === type)
      if (!matches.length) return null
      const rank = s => (s === 'REJECTED' ? 3 : s === 'APPROVED' ? 2 : 1)
      matches.sort((a, b) => rank(b.docStatus) - rank(a.docStatus))
      return matches[0].docStatus || 'PENDING'
    },
    docSlotClass(s) {
      if (s === 'APPROVED') return 'req-step-ok'
      if (s === 'REJECTED') return 'req-step-bad'
      if (s === 'PENDING')  return 'req-step-warn'
      return 'req-step-todo'
    },
    docSlotLabel(s) {
      if (s === 'APPROVED') return 'одобрен'
      if (s === 'REJECTED') return 'отклонён'
      if (s === 'PENDING')  return 'на рассмотрении'
      return 'не загружен'
    },
    openRequestModal(r) {
      if (!r) return
      this.activeRequestId = r.requestId
      this.requestModalOpen = true
      this.loadRequestTimeline(r.requestId)
    },
    loadRequestTimeline(reqId) {
      if (!reqId) return
      if (this.requestTimelines[reqId] || this.requestTimelinesLoading[reqId]) return
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      this.$set(this.requestTimelinesLoading, reqId, true)
      axios.get(`${apiDomain}/exchange/userdetails/${userId}/classification-requests/${reqId}/timeline`, { headers: getHeader() })
        .then(res => {
          this.$set(this.requestTimelines, reqId, Array.isArray(res.data) ? res.data : [])
        })
        .catch(err => {
          console.error('timeline load:', err)
          this.$set(this.requestTimelines, reqId, [])
        })
        .finally(() => { this.$set(this.requestTimelinesLoading, reqId, false) })
    },
    // Reviewer line for an approved/rejected file. Prefers the
    // admin's email; falls back to a humanised actor type when the
    // join didn't find one (system / amlbot).
    // Russian label for "who reviews this doc type" — used in the
    // disabled-button note so an MLRO opening a CERTIFICATE card sees
    // "Подтверждает Compliance Officer" instead of an unexplained
    // grey button.
    reviewerLabel(docType) {
      const ROLE = {
        QUALIFIED:            'MLRO',
        PROFESSIONAL_SOF:     'MLRO',
        CERTIFICATE:          'Compliance Officer',
        PROFESSIONAL_CERT:    'Compliance Officer',
        PROFESSIONAL_HISTORY: 'Compliance Officer',
      }
      return ROLE[docType] || 'Админ'
    },
    // Mirror of canReviewDocType on the backend so the buttons only
    // show for the admin who actually has authority over the doc.
    // ADMIN bypasses the split.
    canReviewDoc(docType) {
      if (this.canActAsAdmin) return true
      const ROLE_BY_TYPE = {
        QUALIFIED:            'MLRO',
        PROFESSIONAL_SOF:     'MLRO',
        CERTIFICATE:          'COMPILANCE',
        PROFESSIONAL_CERT:    'COMPILANCE',
        PROFESSIONAL_HISTORY: 'COMPILANCE',
      }
      const required = ROLE_BY_TYPE[docType]
      if (!required) return false
      return this.currentRoleNames.has(required)
    },
    docDecisionActor(doc) {
      if (!doc) return ''
      const ROLES = {
        ADMIN:      'Админ',
        MANAGER:    'Менеджер',
        OPERATOR:   'Оператор',
        MLRO:       'MLRO',
        COMPILANCE: 'Compliance Officer',
      }
      const role = doc.decidedByRole ? (ROLES[doc.decidedByRole] || doc.decidedByRole) : ''
      const email = doc.decidedByEmail || ''
      if (role && email) return `${role} · ${email}`
      if (email) return email
      if (role)  return role
      const t = doc.decidedByType
      if (t === 'admin')  return 'Админ'
      if (t === 'system') return 'Система'
      if (t === 'amlbot') return 'AMLBot'
      return '—'
    },
    actorLabel(e) {
      if (!e) return '—'
      if (e.changedByEmail) return e.changedByEmail
      if (e.changedByType === 'system')   return 'Система'
      if (e.changedByType === 'amlbot')   return 'AMLBot'
      if (e.changedByType === 'backfill') return 'Миграция'
      return '—'
    },
    actorTypeBadge(t) {
      const map = { admin: 'Админ', user: 'Клиент', system: 'Система', amlbot: 'AMLBot', backfill: 'Миграция' }
      return map[t] || t || ''
    },
    closeRequestModal() {
      this.requestModalOpen = false
      this.activeRequestId = null
    },
    // Mockup uses warm yellow for "pending" requests; we extend the
    // palette so APPROVED/REJECTED also get their own colour. Bg+fg
    // come together so the icon and the badge can share a single
    // call.
    requestIconBg(r) {
      switch (r && r.status) {
        case 'APPROVED': return '#D1FAE5'
        case 'REJECTED': return '#FEE2E2'
        case 'PENDING':  return '#FEF3C7'
        case 'DRAFT':    return '#F0F2F5'
        default:         return '#F0F2F5'
      }
    },
    requestIconColor(r) {
      switch (r && r.status) {
        case 'APPROVED': return '#065F46'
        case 'REJECTED': return '#991B1B'
        case 'PENDING':  return '#92400E'
        case 'DRAFT':    return '#6B7280'
        default:         return '#6B7280'
      }
    },
    // Short "X → Y" path for the list item header.
    shortRequestPath(r) {
      if (!r) return ''
      return this.shortCat(r.fromCategory) + ' → ' + this.shortCat(r.requestedCategory)
    },
    shortCat(c) {
      const map = {
        NONE:         'Без класс.',
        RETAIL_NQ:    'NQ',
        RETAIL_Q:     'Q',
        PROFESSIONAL: 'PRO',
      }
      return map[c] || c || '—'
    },
    requestListSubLabel(r) {
      if (!r) return ''
      if (r.status === 'DRAFT')   return 'Создана ' + this.formatDocDate(r.submittedAt)
      if (r.status === 'PENDING') return 'На рассмотрении с ' + this.formatDocDate(r.submittedAt)
      return 'Подана ' + this.formatDocDate(r.submittedAt)
    },
    docSlotBadge(s) {
      if (s === 'APPROVED') return '✓ Одобрен'
      if (s === 'REJECTED') return '✕ Отклонён'
      if (s === 'PENDING')  return 'На проверке'
      return 'Не загружен'
    },
    // Pick the most "current" attached document of a given type for
    // a request — same precedence the backend uses (REJECTED >
    // APPROVED > PENDING > anything else). Used for compact summaries
    // (badge/label of the slot as a whole).
    docByType(r, type) {
      const all = this.docsByType(r, type)
      return all.length ? all[0] : null
    },
    // Какие документы типа показывать в заявке:
    //   • Документы на рассмотрении (PENDING) — ВСЕ: клиент мог приложить
    //     несколько файлов одного типа в одной подаче (скан + перевод),
    //     их все нужно просмотреть и решить.
    //   • Если на рассмотрении ничего нет — только ПОСЛЕДНИЙ решённый
    //     документ. Так замещённые переподачей REJECTED-ряды скрываются и
    //     старые причины отказа не дублируются.
    // Консистентно с backend RecomputeStatus («freshest upload wins»).
    docsByType(r, type) {
      const docs = (r && r.documents) || []
      const matches = docs.filter(d => d.docType === type)
      if (!matches.length) return []
      const byId = (a, b) => Number(a.docId) - Number(b.docId)
      const pending = matches.filter(d => (d.docStatus || '').toUpperCase() === 'PENDING')
      if (pending.length) return pending.slice().sort(byId)
      const latest = matches.reduce((a, b) => (Number(b.docId) > Number(a.docId) ? b : a))
      return [latest]
    },
    // Замещённые переподачей решённые документы типа (которых нет в текущем
    // показе docsByType) — для аудита прошлых отказов. Старые → новые.
    pastDecisionsByType(r, type) {
      const docs = (r && r.documents) || []
      const matches = docs.filter(d => d.docType === type)
      if (matches.length < 2) return []
      const currentIds = new Set(this.docsByType(r, type).map(d => d.docId))
      return matches
        .filter(d => !currentIds.has(d.docId))
        .filter(d => {
          const s = (d.docStatus || '').toUpperCase()
          return s === 'APPROVED' || s === 'REJECTED'
        })
        .sort((a, b) => Number(a.docId) - Number(b.docId))
    },
    togglePastDecisions(key) {
      this.$set(this.pastOpen, key, !this.pastOpen[key])
    },
    // Открыть модалку отклонения документа — причина обязательна и уходит
    // клиенту в письме об отказе в реклассификации.
    openRejectDoc(doc) {
      if (!doc || !doc.docId || !this.canReviewDoc(doc.docType)) return
      this.rejectDocFor = doc
      this.rejectDocReason = ''
      this.rejectDocInternal = ''
      this.$bvModal.show('reject-doc-modal')
    },
    // Подтверждение из модалки: отклоняем документ с двумя причинами —
    // клиентской и внутренней (обе обязательны).
    confirmRejectDoc() {
      if (!this.rejectDocFor || !this.rejectDocReason.trim() || !this.rejectDocInternal.trim()) return
      this.rejectDocBusy = true
      this.reviewDoc(this.rejectDocFor, 'REJECTED', this.rejectDocReason.trim(), null, this.rejectDocInternal.trim())
        .finally(() => {
          this.rejectDocBusy = false
          this.rejectDocFor = null
          this.rejectDocReason = ''
          this.rejectDocInternal = ''
        })
    },
    // SoF-документы (источник средств) — по ним MLRO задаёт лимит вручную.
    isSofDoc(docType) {
      return docType === 'QUALIFIED' || docType === 'PROFESSIONAL_SOF'
    },
    defaultLimitForCategory(cat) {
      if (cat === 'PROFESSIONAL') return 1000000
      if (cat === 'RETAIL_Q') return 100000
      return ''
    },
    // Разделение разрядов пробелом: 100000 → "100 000".
    formatMoney(v) {
      if (v == null || v === '') return ''
      const n = Number(v)
      if (!Number.isFinite(n)) return String(v)
      return n.toLocaleString('ru-RU')
    },
    // Клик «Одобрить»: для SoF-документа открываем модалку с ручным
    // лимитом; для прочих (сертификат/история) — одобряем сразу.
    approveDocClick(doc) {
      if (!doc || !doc.docId || !this.canReviewDoc(doc.docType)) return
      if (this.isSofDoc(doc.docType)) {
        this.openApproveLimit(doc)
      } else {
        this.openApproveComment(doc)
      }
    },
    openApproveLimit(doc) {
      this.approveDocFor = doc
      this.approveComment = ''
      const cat = this.activeRequest ? this.activeRequest.requestedCategory : ''
      // Дефолт/максимум — исторический fallback сразу, затем актуальный из
      // конфига (админ мог изменить). Предзаполняем максимумом.
      const def = this.defaultLimitForCategory(cat)
      this.approveLimitMax = def === '' ? null : Number(def)
      this.approveLimitValue = def === '' ? '' : String(def)
      this.$bvModal.show('approve-limit-modal')
      axios.get(`${apiDomain}/admin/classification-limits`, { headers: getHeader() })
        .then(res => {
          const found = (Array.isArray(res.data) ? res.data : []).find(l => l.category === cat)
          if (found && this.approveDocFor === doc) {
            this.approveLimitMax = Number(found.maxLimit)
            this.approveLimitValue = String(found.maxLimit)
          }
        })
        .catch(() => {})
    },
    confirmApproveLimit() {
      if (!this.approveDocFor) return
      const limit = Number(this.approveLimitValue)
      if (!Number.isFinite(limit) || limit < 0) return
      this.approveDocBusy = true
      this.reviewDoc(this.approveDocFor, 'APPROVED', null, limit, this.approveComment.trim())
        .finally(() => {
          this.approveDocBusy = false
          this.approveDocFor = null
          this.approveLimitValue = ''
          this.approveComment = ''
        })
    },
    // Одобрение не-SoF документа: открываем модалку с необязательным
    // комментарием для внутреннего аудита.
    openApproveComment(doc) {
      this.approveDocFor = doc
      this.approveComment = ''
      this.$bvModal.show('approve-comment-modal')
    },
    confirmApproveComment() {
      if (!this.approveDocFor) return
      this.approveDocBusy = true
      this.reviewDoc(this.approveDocFor, 'APPROVED', null, null, this.approveComment.trim())
        .finally(() => {
          this.approveDocBusy = false
          this.approveDocFor = null
          this.approveComment = ''
        })
    },
    reviewDoc(doc, status, reasonOverride, limit, internalOverride) {
      if (!doc || !doc.docId) return Promise.resolve()
      // Клиентская причина (mlro_desc) — только при отклонении; при одобрении
      // клиенту ничего не пишем. Внутренний комментарий — опционально.
      const comment = (reasonOverride != null ? reasonOverride : '').trim()
      const internal = (internalOverride != null ? internalOverride : '').trim()
      if (status === 'REJECTED' && (!comment || !internal)) {
        this.$bvToast && this.$bvToast.toast('Укажите обе причины: для клиента и для внутреннего аудита.', {
          title: 'Нужны причины', variant: 'warning',
          toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 3500,
        })
        return Promise.resolve()
      }
      this.$set(this.docReviewing, doc.docId, true)
      // /ptest/changeclientstatus is the existing MLRO/Compliance
      // decision endpoint — it persists doc_status + mlro_desc,
      // attaches the doc to the active classification request if
      // it isn't yet, then runs RecomputeStatus which transitions
      // the request and (for APPROVED) promotes the user category.
      const payload = {
        docId:    doc.docId,
        userId:   this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id),
        docType:  doc.docType,
        status:   status,
        mlroDesc: comment,
      }
      // Внутренний комментарий (аудит): при отклонении обязателен, при
      // одобрении — опционально. Шлём, когда он есть.
      if (internal) {
        payload.internalDesc = internal
      }
      // Ручной лимит — только при одобрении SoF-документа (число >= 0).
      if (typeof limit === 'number' && Number.isFinite(limit) && limit >= 0) {
        payload.limit = limit
      }
      return axios
        .post(`${apiDomain}/ptest/changeclientstatus`, payload, { headers: getHeader() })
        .then(() => {
          this.$bvToast && this.$bvToast.toast(
            status === 'APPROVED' ? 'Документ одобрен.' : 'Документ отклонён.',
            { title: 'Готово', variant: 'success', toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 2500 }
          )
          this.$set(this.docComments, doc.docId, '')
          // Refresh dependent feeds: request list (status may have
          // flipped to APPROVED/REJECTED/UNDER_REVIEW), user header
          // (limit + category may have moved), and docs tab.
          this.requestsLoaded = false
          this.loadRequests()
          this.loadUserData(this.resolvedUserId)
          this.docsLoaded = false
          if (this.activeTab === 'files') this.loadDocs()
          // Notify the host (KYC list side-panel) so its table row —
          // "Лимит/мес", категория, статус — refreshes live without a
          // manual page reload after a reclassification decision.
          this.$emit('user-updated', this.resolvedUserId)
          // Drop the timeline cache so the freshly-written snapshot
          // row appears the next time the modal renders the section.
          if (this.activeRequestId) {
            this.$delete(this.requestTimelines, this.activeRequestId)
            this.loadRequestTimeline(this.activeRequestId)
          }
        })
        .catch(err => {
          const sc = err && err.response && err.response.status
          const data = err && err.response && err.response.data
          let msg = 'Не удалось сохранить решение.'
          if (sc === 422 && typeof data === 'string' && data.indexOf('limit-exceeds-max') !== -1) {
            const max = data.split(':')[1]
            msg = 'Лимит превышает максимально разрешённый для этой классификации' +
              (max ? (' — ' + this.formatMoney(max) + ' $/мес.') : '.')
          } else if (sc === 422 && typeof data === 'string' && data.indexOf('professional-risk-consents-missing') !== -1) {
            msg = 'PROFESSIONAL: пользователь не подтвердил согласия о рисках.'
          } else if (sc === 401 || sc === 403) {
            msg = 'Недостаточно прав для решения.'
          }
          this.$bvToast && this.$bvToast.toast(msg, {
            title: 'Ошибка', variant: 'danger',
            toaster: 'b-toaster-top-center', solid: true, autoHideDelay: 5000,
          })
          console.error('reviewDoc error:', err)
        })
        .finally(() => {
          this.$set(this.docReviewing, doc.docId, false)
        })
    },
    docTypeShort(t) {
      const map = {
        QUALIFIED:           'Источник средств (SoF)',
        CERTIFICATE:         'Сертификат CFA/FRM/CAIA/CIIA',
        PROFESSIONAL_SOF:    'Источник средств (Professional)',
        PROFESSIONAL_CERT:   'Сертификат / диплом',
        PROFESSIONAL_HISTORY:'Торговая история',
        PROFESSIONAL:        'Архив документов',
      }
      return map[t] || t || '—'
    },
    onAttemptsTabClicked() {
      if (this.attemptsLoaded || this.attemptsLoading) return
      this.loadAttempts()
    },
    loadAttempts() {
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      this.attemptsLoading = true
      Promise.all([
        axios.get(`${apiDomain}/exchange/userdetails/${userId}/test-attempts`,   { headers: getHeader() }).then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
        axios.get(`${apiDomain}/exchange/userdetails/${userId}/survey-attempts`, { headers: getHeader() }).then(r => Array.isArray(r.data) ? r.data : []).catch(() => []),
      ]).then(([tests, surveys]) => {
        const merged = tests.concat(surveys)
        merged.sort((a, b) => {
          const ta = a.startedAt ? Date.parse(a.startedAt) : 0
          const tb = b.startedAt ? Date.parse(b.startedAt) : 0
          return tb - ta
        })
        this.attempts = merged
        this.attemptsLoaded = true
      }).finally(() => { this.attemptsLoading = false })
    },
    isSurveyAttempt(code) {
      return typeof code === 'string' && code.indexOf('SURVEY:') === 0
    },
    // Click handler on an attempt row — expands the answers panel and
    // Switch to the "Тестирование" tab and expand the row the user
    // clicked from the Qual summary, so a single click drills from
    // the condensed list into the full attempt + per-question detail.
    goToAttempt(a) {
      this.activeTab = 'tests'
      this.onAttemptsTabClicked()
      this.$nextTick(() => {
        if (!a || a.attemptId == null) return
        if (!this.attemptOpen[a.attemptId]) this.toggleAttempt(a)
      })
    },
    // ── Quals tab helpers (collapsible test items + recls votes) ──
    toggleQTest(id) {
      if (id == null) return
      const open = !this.qTestOpen[id]
      this.$set(this.qTestOpen, id, open)
      if (!open) return
      // Reuse the "Тестирование" tab's lazy-fetch path so per-question
      // detail lands in the shared attemptDetails cache. We have to
      // find the original attempt object because toggleAttempt's
      // fetch needs (attemptId, testCode) to pick the right endpoint
      // (test-attempts vs survey-attempts).
      const a = (this.attempts || []).find(x => x && x.attemptId === id)
      if (!a) return
      if (this.attemptDetails[id] || this.attemptDetailsLoading[id]) return
      // Tap into the canonical loader but skip its own open-toggle by
      // forcing attemptOpen=true first (toggleAttempt early-returns
      // when called twice in a row, so we use the same effect by
      // pre-marking it open and re-calling).
      if (!this.attemptOpen[id]) this.toggleAttempt(a)
    },
    // Map an attempt to one of the mock's three q-badge classes.
    // Logic mirrors attemptVerdictLabel above, but reuses the same
    // backend signals (a.passed + testCodeHasVerdict) rather than
    // parsing the CSS class string, which returns 'doc-status-*' and
    // not 'pass/fail' — that mismatch was making every row read
    // "Ожидает".
    qTestBadgeClass(a) {
      if (!a) return 'q-badge-pend'
      if (a.passed === false) return 'q-badge-fail'
      // Surveys aren't pass/fail — a completed survey row is "Пройден".
      if (!this.testCodeHasVerdict(a.testCode)) return 'q-badge-pass'
      if (a.passed === true)  return 'q-badge-pass'
      return 'q-badge-pend'
    },
    qTestBadgeLabel(a) {
      if (!a) return 'Ожидает'
      if (a.passed === false) return 'Не пройден'
      if (!this.testCodeHasVerdict(a.testCode)) return 'Пройден'
      if (a.passed === true)  return 'Пройден'
      return 'Ожидает'
    },
    // Per-testCode flavour text shown in the expanded q-test-body.
    // Keeps the mock's tone — a short editorial blurb so the screen
    // doesn't look bare when there are no per-question details yet.
    qTestDescription(code) {
      const map = {
        ONBOARDING:   'Базовая анкета при регистрации. Вопросы об опыте инвестирования и финансовых целях.',
        QUALIFIED:    'Тест на знание финансовых инструментов и рисков криптоактивов.',
        PROFESSIONAL: 'Расширенный тест для получения статуса профессионального инвестора.',
      }
      return map[(code || '').toUpperCase()] || 'Опросник по классификации клиента.'
    },
    // Final-result label/colours for a reclass row — derived from the
    // top-level request status until per-vote (compliance / MLRO)
    // signals are wired separately.
    qReclsResultLabel(r) {
      const s = (r && r.status || '').toUpperCase()
      if (s === 'APPROVED') return '✓ Одобрена'
      if (s === 'REJECTED') return '✕ Отклонена'
      return '⏳ На рассмотрении'
    },
    qReclsResultClass(r) {
      const s = (r && r.status || '').toUpperCase()
      if (s === 'APPROVED') return 'qrr-approved'
      if (s === 'REJECTED') return 'qrr-rejected'
      return 'qrr-pending'
    },
    // Решение роли (compliance / MLRO) по заявке выводим из статусов её
    // документов: MLRO ревьюит источник средств (QUALIFIED /
    // PROFESSIONAL_SOF), Compliance — сертификат и историю торгов. Есть
    // отклонённый документ роли → отказ; все решены → одобрение; иначе
    // «Ожидает решения» (null). Fallback: терминально одобренная заявка
    // без документного следа роли считается одобренной этой ролью.
    qVote(request, role) {
      if (!request) return null
      const docTypes = role === 'mlro'
        ? ['QUALIFIED', 'PROFESSIONAL_SOF']
        : ['CERTIFICATE', 'PROFESSIONAL_CERT', 'PROFESSIONAL_HISTORY']
      const docs = ((request.documents) || []).filter(d => docTypes.indexOf(d.docType) !== -1)
      // Берём только ПОСЛЕДНИЙ документ каждого типа (актуальный после
      // переподач) — консистентно с backend RecomputeStatus («freshest
      // upload wins»). Иначе при повторном отказе показывалась старая
      // причина вместо свежей.
      const latestByType = {}
      docs.forEach(d => {
        const t = d.docType
        if (!latestByType[t] || Number(d.docId) > Number(latestByType[t].docId)) latestByType[t] = d
      })
      const current = Object.keys(latestByType).map(k => latestByType[k])
      const decided = current.filter(d => {
        const s = (d.docStatus || '').toUpperCase()
        return s === 'APPROVED' || s === 'REJECTED'
      })
      if (decided.length) {
        const byId = (a, b) => Number(a.docId) - Number(b.docId)
        const rejected = decided.filter(d => (d.docStatus || '').toUpperCase() === 'REJECTED').sort(byId)
        const chosen = rejected.length ? rejected[rejected.length - 1] : decided.slice().sort(byId)[decided.length - 1]
        return {
          action: rejected.length ? 'reject' : 'approve',
          comment: chosen.mlroDesc || '',
          internal: chosen.internalDesc || '',
          when: this.formatDocDate(chosen.decidedAt) || '',
        }
      }
      if ((request.status || '').toUpperCase() === 'APPROVED') {
        // Терминально одобренная заявка без документного следа роли: показываем
        // «Одобрил» ТОЛЬКО если решение роли реально требуется. Комплаенс при
        // пути через тест (клиент прошёл тест, сертификат не прикладывал) в
        // заявке не участвует — не рисуем ложное одобрение; шаблон покажет
        // «Не требуется (путь через тест)».
        if (role === 'compliance' && !this.requestNeedsCompliance(request)) {
          return null
        }
        return { action: 'approve', comment: '', internal: '', when: '' }
      }
      return null
    },
    qVoteColor(request, role) {
      const vote = this.qVote(request, role)
      return vote && vote.action === 'reject' ? '#DC2626' : '#065F46'
    },
    qVoteBorder(request, role) {
      const vote = this.qVote(request, role)
      if (!vote) return '#EAECF0'
      return vote.action === 'reject' ? '#FCA5A5' : '#6EE7B7'
    },
    // changed_by_type → пользовательский ярлык. Совпадает с актёрами,
    // которые пишет audit-actor session: admin / amlbot / system /
    // backfill / user.
    qualActorLabel(by) {
      const map = {
        admin:    'Администратор',
        amlbot:   'AMLBot',
        system:   'Система',
        backfill: 'Миграция',
        user:     'Клиент',
      }
      return map[by] || by || '—'
    },
    // lazy-fetches the per-question data on first open. Surveys and
    // tests use the same code path; the URL is just keyed by isSurvey.
    toggleAttempt(a) {
      if (!a || a.attemptId == null) return
      const id = a.attemptId
      const open = !this.attemptOpen[id]
      this.$set(this.attemptOpen, id, open)
      if (!open) return
      if (this.attemptDetails[id] || this.attemptDetailsLoading[id]) return
      const userId = this.resolvedUserId || (this.loadedUserData && this.loadedUserData.id)
      if (!userId) return
      const isSurvey = this.isSurveyAttempt(a.testCode)
      const base = isSurvey ? 'survey-attempts' : 'test-attempts'
      this.$set(this.attemptDetailsLoading, id, true)
      axios.get(`${apiDomain}/exchange/userdetails/${userId}/${base}/${id}/answers`, { headers: getHeader() })
        .then(res => {
          this.$set(this.attemptDetails, id, Array.isArray(res.data) ? res.data : [])
        })
        .catch(err => {
          console.error('attempt answers error:', err)
          this.$set(this.attemptDetails, id, [])
        })
        .finally(() => {
          this.$set(this.attemptDetailsLoading, id, false)
        })
    },
    // True for tests where wrong/right matters (BASIC, QUALIFIED).
    // PROFESSIONAL is a survey-style compliance form even though it
    // sits in p_test_answer_response — its answers aren't graded.
    testCodeHasVerdict(code) {
      // BASIC/QUALIFIED/PROFESSIONAL — оцениваемые (pass/fail); опросы — нет.
      return !this.isSurveyAttempt(code)
    },
    // Вердикт на уровне ОТВЕТА: у проф-теста профильные вопросы
    // (q.isProfile) не оцениваются — показываем без зелёного/красного и
    // без эталона. Для оцениваемых (BASIC/QUALIFIED и неподпрофильные
    // PROFESSIONAL) — вердикт есть.
    answerHasVerdict(a, q) {
      if (!a || !q) return false
      if (this.isSurveyAttempt(a.testCode)) return false
      if (a.testCode === 'PROFESSIONAL') return !q.isProfile
      return true
    },
    // Tint each answer row: green for correct, red for wrong, plain
    // for ungraded surveys / profile questions.
    answerItemClass(a, q) {
      if (!this.answerHasVerdict(a, q)) return ''
      if (q.isCorrect === 1) return 'answer-ok'
      if (q.isCorrect === 0) return 'answer-bad'
      return ''
    },
    testCodeLabel(code) {
      if (this.isSurveyAttempt(code)) {
        const sub = code.slice('SURVEY:'.length)
        return sub === 'ONBOARDING' ? 'Анкета онбординга' : ('Анкета: ' + sub)
      }
      switch (code) {
        case 'BASIC':        return 'Базовый тест'
        case 'QUALIFIED':    return 'Квалификационный тест'
        case 'PROFESSIONAL': return 'Тест (PROFESSIONAL)'
        default:             return code || '—'
      }
    },
    formatScreeningDate(iso) {
      if (!iso) return '—'
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
    },
    // Strip the time component from ISO timestamps like
    // "1985-11-07T00:00:00Z" so birth dates / document dates render as
    // DD.MM.YYYY. Plain "YYYY-MM-DD" strings come back the same way.
    formatDateOnly(v) {
      if (!v) return ''
      const s = String(v)
      // Already a bare date — just rewrite to DD.MM.YYYY for parity.
      const bare = s.match(/^(\d{4})-(\d{2})-(\d{2})$/)
      if (bare) return `${bare[3]}.${bare[2]}.${bare[1]}`
      const d = new Date(s)
      if (isNaN(d.getTime())) return s
      const pad = n => (n < 10 ? '0' + n : '' + n)
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`
    },
  },

  created() {
    this.listCountries()
    this.listDocTypes()
    const userId = this.resolvedUserId
    this.loadUserData(userId)
    this.loadSnapshots()
    // Preload classification requests eagerly so we know whether to
    // promote "Заявки" to the first tab.
    this.loadRequests()
  },

  watch: {
    resolvedUserId(newUserId) {
      if (!newUserId) return
      this.loadUserData(newUserId)
      this.applicantRaw = null
      this.lastApplicantLoaded = false
      // Refetch requests for the new user too.
      this.requestsLoaded = false
      this.loadRequests()
    },
    // When the request list resolves and the user is still on the
    // default "Идентификация" tab, auto-jump to the (now visible)
    // "Заявки" tab. We only override the default — manual switches
    // are respected.
    requests() {
      const hasActive = this.activeRequests && this.activeRequests.length
      if (hasActive && this.activeTab === 'ident') {
        this.activeTab = 'requests'
      }
      if (!hasActive && this.activeTab === 'requests') {
        this.activeTab = 'ident'
      }
    }
  }
}
</script>

<style scoped>
/* ====================================================================
   UserDetail — KYC_interactive.html parity. The component renders the
   same chrome regardless of host (Users.vue side panel = panel mode,
   /userdetail/:id = page mode); the wrapping container handles width.
   ==================================================================== */

.ud-root {
  background: #fff;
  font-family: 'Inter', system-ui, -apple-system, Segoe UI, sans-serif;
  color: #0A0A0A;
  font-size: 14px;
  width: 100%;
  display: flex;
  flex-direction: column;
}
.ud-root, .ud-root * { box-sizing: border-box; }

/* ---- sp-head ---- */
.sp-head {
  padding: 16px 60px 16px 20px;
  border-bottom: 1px solid #F0F2F5;
  background: #fff;
  flex: 0 0 auto;
}
.sp-client {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}
.sp-client-info { min-width: 0; flex: 1 1 auto; }
.sp-avatar {
  width: 48px; height: 48px;
  border-radius: 50%;
  background: #EDFAF5;
  color: #0D7A5F;
  display: flex; align-items: center; justify-content: center;
  font-size: 16px; font-weight: 800;
  flex-shrink: 0;
}
.sp-name {
  font-size: 18px; font-weight: 700; color: #0A0A0A;
  letter-spacing: -0.01em; line-height: 1.25;
  word-break: break-word;
}
.sp-email {
  font-size: 13px; color: #9CA3AF; margin-top: 2px;
  word-break: break-all;
}
.sp-id { font-size: 12px; color: #C0C4CC; margin-top: 2px; letter-spacing: 0.04em; }

/* ---- sp-status ---- */
.sp-status {
  padding: 12px 20px;
  background: #F9FAFB;
  border-bottom: 1px solid #F0F2F5;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  flex: 0 0 auto;
}
.sp-stat { min-width: 0; }
.sp-stat label {
  display: block;
  font-size: 10px; font-weight: 700;
  color: #9CA3AF; letter-spacing: 0.07em; text-transform: uppercase;
  margin: 0 0 4px 0;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.sp-stat .val {
  font-size: 13px; font-weight: 700; color: #0A0A0A;
  word-break: break-word; overflow-wrap: anywhere;
}
.val-ok   { color: #065F46; }
.val-high { color: #DC2626; }
.val-warn { color: #D97706; }

/* ---- sp-tabs ---- */
.sp-tabs {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  overflow-y: hidden;
  border-bottom: 1px solid #F0F2F5;
  background: #fff;
  padding: 0 12px;
  flex: 0 0 auto;
  scrollbar-width: thin;
}
.sp-tabs::-webkit-scrollbar { height: 4px; }
.sp-tabs::-webkit-scrollbar-thumb { background: #EAECF0; border-radius: 2px; }
.sp-tabs::-webkit-scrollbar-track { background: transparent; }
.sp-tab {
  padding: 11px 14px;
  font-size: 13px; font-weight: 600;
  color: #9CA3AF;
  border-bottom: 2px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  flex: 0 0 auto;
  transition: color 0.12s, border-color 0.12s;
}
.sp-tab:hover { color: #374151; }
.sp-tab.active {
  color: #0D7A5F;
  border-bottom-color: #16A37F;
  font-weight: 700;
}
/* Right-aligned tabs (e.g. "Действия") only flex-grow into auto-margin
   when there is room. With nowrap+scroll we just colour them. */
.sp-tab-right { color: #16A37F; }

/* ---- sp-body ---- */
.sp-body {
  padding: 16px 20px 24px;
  background: #fff;
  flex: 1 1 auto;
  overflow: visible;
}

/* 1:1 with KYC_interactive .sp-section / .sf / .fv — slightly smaller
   eyebrow labels and tighter field pills than what we had before, so
   the dossier reads like the mock instead of like a beefier admin form. */
.sp-section { margin-bottom: 20px; }
.sp-section:last-child { margin-bottom: 0; }
.sp-section-title {
  font-size: 9px; font-weight: 700;
  color: #9CA3AF; letter-spacing: 0.09em; text-transform: uppercase;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #F0F2F5;
}

.sp-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.sf {
  background: #F9FAFB;
  border-radius: 8px;
  padding: 9px 12px;
  min-width: 0;
}
.sf.full { grid-column: 1 / -1; }
.sf label {
  display: block;
  font-size: 9px; font-weight: 700;
  color: #9CA3AF; letter-spacing: 0.06em; text-transform: uppercase;
  margin-bottom: 3px;
}
.sf .fv {
  font-size: 12px; font-weight: 600;
  color: #0A0A0A;
  word-break: break-word; overflow-wrap: anywhere;
}
.sf .fv.empty {
  color: #D1D5DB;
  font-style: italic;
  font-weight: 400;
  font-size: 11px;
}
.sf .fv.fv-ok   { color: #065F46; font-weight: 700; }
.sf .fv.fv-high { color: #DC2626; font-weight: 800; }

/* ---- empty / placeholder rows ---- */
.sp-empty {
  text-align: center;
  font-size: 13px;
  color: #9CA3AF;
  padding: 24px 0;
}

/* ---- Files list ---- */
/* AMLBot KYC-документы: группы по типу + сетка превью. */
.kyc-doc-group { margin-bottom: 14px; }
.kyc-doc-group-title {
  font-size: 12px;
  font-weight: 700;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: .04em;
  margin: 0 0 8px 0;
}
.kyc-doc-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
}
.kyc-doc-file {
  border: 1px solid #EAECF0;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
  cursor: pointer;
  transition: border-color .12s, box-shadow .12s;
}
.kyc-doc-file:hover { border-color: #93C5FD; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
.kyc-doc-thumb-img {
  display: block;
  width: 100%;
  height: 90px;
  object-fit: cover;
  background: #F0F2F5;
}
.kyc-doc-thumb-ph {
  height: 90px;
  display: flex; align-items: center; justify-content: center;
  background: #F0F2F5;
  color: #6B7280;
  font: 700 13px/1 Inter;
}
.kyc-doc-file-side {
  padding: 6px 8px;
  font-size: 11px;
  color: #374151;
  border-top: 1px solid #F1F3F5;
}

.doc-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #EAECF0;
  border-radius: 10px;
  margin-bottom: 8px;
  background: #fff;
  cursor: pointer;
  transition: border-color 0.12s, background 0.12s;
}
.doc-item:hover { border-color: #D1D5DB; background: #F9FAFB; }
.doc-thumb {
  width: 36px; height: 36px;
  border-radius: 8px;
  background: #F0F2F5;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  color: #9CA3AF;
}
.doc-thumb svg { width: 18px; height: 18px; }
.doc-info { flex: 1 1 auto; min-width: 0; }
.doc-name { font-size: 12px; font-weight: 600; color: #0A0A0A; }
.doc-meta { font-size: 10px; color: #9CA3AF; margin-top: 2px; }
.doc-status {
  font-size: 10px; font-weight: 700;
  padding: 3px 8px;
  border-radius: 5px;
  flex-shrink: 0;
  margin-left: auto;
  white-space: nowrap;
}
.doc-status-ok  { background: #ECFDF5; color: #065F46; }
.doc-status-req { background: #FFFBEB; color: #92400E; }
.doc-status-bad { background: #FEF2F2; color: #991B1B; }

/* ---- Attempts list ---- */
.attempt-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 11px 12px;
  border: 1px solid #EAECF0;
  border-radius: 9px;
  margin-bottom: 8px;
  background: #fff;
}
.attempt-block {
  border: 1px solid #EAECF0;
  border-radius: 9px;
  margin-bottom: 8px;
  background: #fff;
  overflow: hidden;
}
.attempt-block-open { border-color: #16A37F; }
.attempt-block > .attempt-row {
  border: 0;
  border-radius: 0;
  margin-bottom: 0;
  cursor: pointer;
}
.attempt-block > .attempt-row:hover { background: #F9FAFB; }
.attempt-row-main { flex: 1 1 auto; min-width: 0; }
.attempt-title { font-size: 14px; font-weight: 600; color: #0A0A0A; }
.attempt-meta { font-size: 12px; color: #9CA3AF; margin-top: 3px; }
.attempt-verdict {
  font-size: 11px; font-weight: 700;
  padding: 4px 9px;
  border-radius: 5px;
  flex-shrink: 0;
}
.attempt-chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  color: #9CA3AF;
  margin-left: 4px;
  transition: transform 0.18s ease;
}
.attempt-block-open .attempt-chevron { transform: rotate(180deg); color: #16A37F; }

.attempt-answers {
  border-top: 1px solid #F0F2F5;
  background: #F9FAFB;
  padding: 12px 14px;
}
.answer-list {
  list-style: decimal;
  padding-left: 22px;
  margin: 0;
}
.answer-item {
  padding: 9px 11px 11px;
  background: #fff;
  border: 1px solid #EAECF0;
  border-radius: 7px;
  margin-bottom: 8px;
}
.answer-item:last-child { margin-bottom: 0; }
.answer-item.answer-ok  { border-left: 3px solid #16A37F; }
.answer-item.answer-bad { border-left: 3px solid #DC2626; }
.answer-q {
  font-size: 13px; font-weight: 600; color: #0A0A0A;
  margin-bottom: 4px;
  word-break: break-word;
}
.answer-a {
  font-size: 13px; color: #374151;
  word-break: break-word;
}
.answer-correct {
  font-size: 12px; color: #065F46;
  margin-top: 6px;
  background: #ECFDF5;
  border-radius: 5px;
  padding: 5px 8px;
}
.answer-profile {
  font-size: 12px; color: #6B7280;
  margin-top: 6px;
  background: #F3F4F6;
  border-radius: 5px;
  padding: 5px 8px;
  font-style: italic;
}

/* ---- Action buttons (Actions tab) ---- */
.action-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
}
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 14px;
  border-radius: 9px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  border: 1px solid transparent;
  transition: background 0.12s, border-color 0.12s;
}
.action-btn-block  { background: #FEF2F2; color: #991B1B; border-color: #FECACA; }
.action-btn-block:hover  { background: #FEE2E2; }
.action-btn-unblock{ background: #ECFDF5; color: #065F46; border-color: #A7F3D0; }
.action-btn-unblock:hover{ background: #D1FAE5; }
.action-btn-ghost  { background: #F3F4F6; color: #374151; border-color: #E5E7EB; }
.action-btn-ghost:hover  { background: #E5E7EB; }

/* ---- Snapshot rows (Actions tab) ---- */
.snap-row {
  padding: 11px 12px;
  border: 1px solid #EAECF0;
  border-radius: 9px;
  margin-bottom: 8px;
  background: #fff;
}
.snap-row-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.snap-actor { font-size: 13px; font-weight: 700; color: #0A0A0A; }
.snap-date  { font-size: 12px; color: #9CA3AF; }
.snap-row-body { font-size: 13px; color: #374151; word-break: break-word; }

/* ---- Classification requests — mockup parity ---- */
.no-req {
  text-align: center;
  padding: 24px 0;
  font-size: 13px;
  color: #9CA3AF;
}
.req-list-item {
  border: 1px solid #EAECF0;
  border-radius: 10px;
  padding: 13px 14px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.15s;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 12px;
}
.req-list-item:hover {
  border-color: #16A37F;
  background: #F0FBF8;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(22,163,127,0.08);
}
.req-list-icon {
  width: 36px; height: 36px;
  border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.req-list-icon svg {
  width: 16px; height: 16px;
}
.req-list-body { flex: 1 1 auto; min-width: 0; }
.req-list-title { font-size: 12px; font-weight: 700; color: #0A0A0A; margin-bottom: 2px; }
.req-list-meta  { font-size: 10px; color: #9CA3AF; }
.req-list-arrow {
  color: #9CA3AF;
  font-size: 16px;
  flex-shrink: 0;
  transition: color 0.12s;
}
.req-list-item:hover .req-list-arrow { color: #16A37F; }
.req-type-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 5px;
  font-size: 9px;
  font-weight: 700;
  margin-left: 8px;
  vertical-align: middle;
}

/* History subsection — KYC_interactive .req-history-section. */
.req-history-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #F0F2F5;
}
.req-history-title {
  font-size: 9px;
  font-weight: 700;
  color: #9CA3AF;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.req-hist-item {
  border: 1px solid #EAECF0;
  border-radius: 9px;
  padding: 11px 13px;
  margin-bottom: 7px;
  background: #fff;
  cursor: pointer;
  transition: border-color .12s, background-color .12s;
}
.req-hist-item:hover {
  border-color: #16A37F;
  background: #F0FBF8;
}
.req-hist-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
}
.req-hist-type {
  font-size: 11px;
  font-weight: 700;
  color: #0A0A0A;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.req-hist-result {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 5px;
}
.rhr-approved { background: #ECFDF5; color: #065F46; }
.rhr-rejected { background: #FEF2F2; color: #991B1B; }
.req-hist-meta { font-size: 10px; color: #9CA3AF; }

/* ── Qual tab — 1:1 port of KYC_interactive .q-test-* / .q-recls-* */
.q-test-item {
  border: 1px solid #EAECF0;
  border-radius: 9px;
  margin-bottom: 7px;
  overflow: hidden;
  background: #fff;
}
.q-test-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 11px 14px;
  cursor: pointer;
  transition: background 0.12s;
}
.q-test-header:hover { background: #F9FAFB; }
.q-test-title { font-size: 12px; font-weight: 700; color: #0A0A0A; }
.q-test-meta  { font-size: 10px; color: #9CA3AF; margin-top: 2px; }
.q-test-right { display: flex; align-items: center; gap: 10px; }
.q-badge-pass { background: #ECFDF5; color: #065F46; font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 5px; }
.q-badge-fail { background: #FEF2F2; color: #991B1B; font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 5px; }
.q-badge-pend { background: #FFFBEB; color: #92400E; font-size: 10px; font-weight: 700; padding: 3px 9px; border-radius: 5px; }
.q-chevron { color: #9CA3AF; font-size: 12px; transition: transform 0.2s; display: inline-block; }
.q-chevron.open { transform: rotate(180deg); }
.q-test-body {
  padding: 0 14px 12px;
  border-top: 1px solid #F0F2F5;
  display: none;
}
.q-test-body.open { display: block; }
.q-test-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 10px;
}
.qtf {
  background: #F9FAFB;
  border-radius: 7px;
  padding: 8px 10px;
}
.qtf label {
  font-size: 9px;
  font-weight: 700;
  color: #9CA3AF;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  display: block;
  margin-bottom: 3px;
}
.qtf .qv { font-size: 12px; font-weight: 600; color: #0A0A0A; }
.qtf .qv.qv-light { font-weight: 400; font-size: 11px; }
.qtf.qtf-span2 { grid-column: span 2; }

/* Reclassification history */
.q-recls-item {
  border: 1px solid #EAECF0;
  border-radius: 9px;
  padding: 13px 14px;
  margin-bottom: 8px;
  background: #fff;
}
.q-recls-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.q-recls-title { font-size: 12px; font-weight: 700; color: #0A0A0A; }
.q-recls-result {
  font-size: 10px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 5px;
}
.qrr-approved { background: #ECFDF5; color: #065F46; }
.qrr-rejected { background: #FEF2F2; color: #991B1B; }
.qrr-pending  { background: #FFFBEB; color: #92400E; }
.q-recls-date { font-size: 10px; color: #9CA3AF; margin-bottom: 8px; }
.q-recls-votes {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 8px;
}
.q-vote {
  border-radius: 7px;
  padding: 9px 11px;
  border: 1px solid #EAECF0;
}
.q-vote-role {
  font-size: 9px;
  font-weight: 700;
  color: #9CA3AF;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  margin-bottom: 4px;
}
.q-vote-result { font-size: 11px; font-weight: 700; margin-bottom: 3px; }
.q-vote-comment { font-size: 10px; color: #6B7280; font-style: italic; }
.q-vote-internal { margin-top: 3px; color: #B45309; font-style: normal; font-weight: 600; }
.q-vote-when    { font-size: 9px; color: #C0C4CC; margin-top: 3px; }
.q-vote-pending { color: #9CA3AF; font-size: 11px; font-style: italic; }
.no-qual {
  font-size: 12px;
  color: #9CA3AF;
  text-align: center;
  padding: 16px 0;
}

/* Category-history mini-timeline under "Текущая классификация".
   Compact vertical list — bullet + label + date · actor. */
.qual-cat-history {
  margin-top: 14px;
  padding-top: 12px;
  border-top: 1px solid #F0F2F5;
}
.qual-cat-history-title {
  font-size: 9px;
  font-weight: 700;
  color: #9CA3AF;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.qual-cat-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
}
.qual-cat-row-bullet {
  flex: 0 0 8px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  background: #9CA3AF;
}
.qual-cat-row-bullet-success { background: #10B981; }
.qual-cat-row-bullet-primary { background: #6366F1; }
.qual-cat-row-body { flex: 1 1 auto; min-width: 0; }
.qual-cat-row-label { font-size: 12px; font-weight: 600; color: #0A0A0A; }
.qual-cat-row-meta  { font-size: 10px; color: #9CA3AF; margin-top: 1px; }

/* ── AML "Изменить риск" form — 1:1 port of KYC_interactive ───── */
.action-label {
  font-size: 9px;
  font-weight: 700;
  color: #9CA3AF;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  display: block;
  margin-bottom: 5px;
}
.action-select {
  width: 100%;
  height: 34px;
  padding: 0 11px;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  background-color: #F9FAFB;
  font-size: 12px;
  color: #111;
  font-family: inherit;
  outline: none;
  transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease;
  font-weight: 500;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='%23aaa' stroke-width='2.5'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 10px center;
  padding-right: 28px;
  cursor: pointer;
}
.action-select:focus {
  border-color: #16A37F;
  background-color: #fff;
  box-shadow: 0 0 0 3px rgba(22, 163, 127, 0.08);
}
.comment-area {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  background: #F9FAFB;
  font-size: 12px;
  color: #111;
  font-family: inherit;
  outline: none;
  resize: vertical;
  min-height: 72px;
  transition: border-color .15s ease, background-color .15s ease, box-shadow .15s ease;
  font-weight: 500;
}
.comment-area:focus {
  border-color: #16A37F;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(22, 163, 127, 0.08);
}
.comment-area::placeholder { color: #C0C4CC; font-weight: 400; }
.action-save-btn {
  width: 100%;
  height: 34px;
  background: #16A37F;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  font-family: inherit;
  transition: background-color .15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}
.action-save-btn:hover:not(:disabled) { background: #138C6C; }
.action-save-btn:disabled { background: #A7F3D0; cursor: not-allowed; }
.action-save-btn svg {
  width: 13px; height: 13px;
  stroke: #fff; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round;
}
/* Пометка «вручную» рядом с эффективным риском + история переопределений */
.risk-manual-tag {
  margin-left: 6px; font-size: 10px; font-weight: 700; text-transform: uppercase;
  padding: 1px 6px; border-radius: 5px; background: rgba(245,158,11,0.15); color: #B45309;
  vertical-align: middle;
}
.risk-history { margin-top: 16px; border-top: 1px solid #ececec; padding-top: 12px; }
.risk-history-title { font-size: 12px; font-weight: 700; color: #667085; margin-bottom: 8px; }
.risk-history-item { padding: 8px 0; border-bottom: 1px solid #f2f4f7; }
.risk-history-top { display: flex; align-items: center; justify-content: space-between; }
.risk-history-level { font-size: 12px; font-weight: 700; }
.risk-history-date { font-size: 11px; color: #98A2B3; }
.risk-history-reason { font-size: 12px; color: #344054; margin-top: 3px; }
.risk-history-actor { font-size: 11px; color: #98A2B3; margin-top: 2px; }

/* ── Actions tab — 1:1 port of KYC_interactive ─────────────────── */
/* 2FA reset CTA: indigo card-like button. */
.twofa-btn {
  width: 100%;
  height: 36px;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #4338CA;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
}
.twofa-btn:hover { background: #E0E7FF; border-color: #A5B4FC; color: #3730A3; }
.twofa-btn svg { width: 14px; height: 14px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.twofa-log { margin-top: 12px; }
.twofa-log-title {
  font-size: 9px;
  font-weight: 700;
  color: #9CA3AF;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  margin-bottom: 8px;
}
.twofa-log-item {
  background: #F9FAFB;
  border-radius: 7px;
  padding: 9px 11px;
  margin-bottom: 6px;
  border: 1px solid #F0F2F5;
}
.twofa-log-date { font-size: 10px; color: #9CA3AF; margin-bottom: 3px; }
.twofa-log-admin { font-size: 11px; font-weight: 600; color: #374151; }
.twofa-log-comment { font-size: 11px; color: #6B7280; margin-top: 3px; font-style: italic; }

/* Block / Unblock subsection — separated from 2FA by a top border. */
.twofa-section {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #F0F2F5;
}
.block-btn-wrap { display: flex; gap: 8px; margin-bottom: 16px; }
.block-btn-do {
  flex: 1;
  height: 36px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background-color .15s ease, border-color .15s ease, color .15s ease;
  border: none;
}
.block-btn-do svg { width: 13px; height: 13px; stroke: currentColor; fill: none; stroke-width: 2; stroke-linecap: round; stroke-linejoin: round; }
.bb-do-block { background: #FEF2F2; color: #991B1B; border: 1px solid #FECACA; }
.bb-do-block:hover { background: #FEE2E2; }
.bb-do-unblock { background: #ECFDF5; color: #065F46; border: 1px solid #A7F3D0; }
.bb-do-unblock:hover { background: #D1FAE5; }
.bb-do-freeze { background: #FFFBEB; color: #92400E; border: 1px solid #FDE68A; }
.bb-do-freeze:hover { background: #FEF3C7; }
.freeze-note { color: #6B7280; font-size: 12.5px; line-height: 1.5; margin: 0 0 12px 0; }
.freeze-state-badge { display: inline-flex; align-items: center; padding: 5px 10px; border-radius: 8px; background: #FFF7ED; color: #B45309; border: 1px solid #FED7AA; font-size: 12.5px; font-weight: 600; }
.blog-type-freeze { color: #B45309; }
.blog-type-unfreeze { color: #065F46; }
.block-log { margin-top: 4px; }
.blog-item {
  padding: 11px 12px;
  border: 1px solid #EAECF0;
  border-radius: 9px;
  margin-bottom: 8px;
  background: #fff;
}
.blog-item-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 5px;
}
.blog-type {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 700;
}
.blog-type-block { color: #991B1B; }
.blog-type-unblock { color: #065F46; }
.blog-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}
.blog-date { font-size: 10px; color: #9CA3AF; }
.blog-admin { font-size: 10px; color: #6B7280; margin-bottom: 4px; }
.blog-reason {
  font-size: 11px;
  color: #374151;
  background: #F9FAFB;
  border-radius: 6px;
  padding: 6px 9px;
  border-left: 2px solid #E5E7EB;
}
.no-log {
  font-size: 12px;
  color: #9CA3AF;
  text-align: center;
  padding: 20px 0;
}

/* "Документы заявки" button — light ghost variant. */
.q-recls-item .hist-open-btn {
  width: 100%;
  height: 30px;
  border: 1px solid #EAECF0;
  border-radius: 7px;
  background: #fff;
  font-size: 11px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  font-family: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: border-color .15s ease, color .15s ease, background-color .15s ease;
}
.q-recls-item .hist-open-btn:hover {
  border-color: #16A37F;
  color: #16A37F;
  background: #F0FBF8;
}

/* ---- Request modal ---- */
.req-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.4);
  z-index: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}
/* 1:1 with KYC_interactive .req-modal — narrower, tighter spacing,
   9px section eyebrow, 12px field value. Per-doc decision controls
   below are domain-specific (our RBAC splits MLRO vs COMPILANCE per
   docType) so the mock's bottom textarea + global Approve/Reject
   isn't ported — each doc carries its own. */
.req-modal {
  background: #fff;
  border-radius: 16px;
  width: 72vw;
  max-width: 900px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0,0,0,0.18);
}
.req-modal-head {
  padding: 20px 24px;
  border-bottom: 1px solid #F0F2F5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.req-modal-title { font-size: 16px; font-weight: 800; color: #0A0A0A; letter-spacing: -0.01em; }
.req-modal-sub   { font-size: 12px; color: #9CA3AF; margin-top: 3px; }
.req-modal-close {
  width: 32px; height: 32px;
  border: 1px solid #EAECF0;
  border-radius: 8px;
  background: #fff;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  color: #9CA3AF;
  transition: background-color .12s, color .12s;
  flex-shrink: 0;
}
.req-modal-close:hover { background: #F5F5F5; color: #374151; }
.req-modal-close svg { width: 14px; height: 14px; stroke-width: 2.5; }

.req-modal-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 20px 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  align-content: start;
}
.req-modal-section { background: #F9FAFB; border-radius: 10px; padding: 14px 16px; }
.req-modal-section.full { grid-column: span 2; }
.req-modal-sec-title {
  font-size: 9px; font-weight: 700;
  color: #9CA3AF; letter-spacing: 0.09em; text-transform: uppercase;
  margin-bottom: 10px;
  padding-bottom: 6px;
  border-bottom: 1px solid #EAECF0;
}
/* Section title with a reviewer badge ("MLRO" / "Комплаенс-офицер") —
   used on the document groups so the reviewer instantly sees who owns
   each block. */
.req-modal-sec-title--with-badge {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.req-modal-sec-title--with-badge > span:first-child {
  font-size: 11px;
  font-weight: 700;
  color: #0A0A0A;
  text-transform: none;
  letter-spacing: 0;
}
.req-reviewer-badge {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 5px;
  letter-spacing: 0;
  text-transform: none;
}
.req-reviewer-badge--mlro {
  background: #D1FAE5;
  color: #065F46;
}
.req-reviewer-badge--compliance {
  background: #E0E7FF;
  color: #4338CA;
}
.req-modal-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.rmf {
  background: #fff;
  border-radius: 7px;
  padding: 8px 10px;
  border: 1px solid #F0F2F5;
}
.rmf.full { grid-column: span 2; }
.rmf label {
  font-size: 9px; font-weight: 700;
  color: #9CA3AF; letter-spacing: 0.06em; text-transform: uppercase;
  display: block; margin-bottom: 3px;
}
.rmf .rv { font-size: 12px; font-weight: 600; color: #0A0A0A; }
.rmf .rv.arrow { color: #16A37F; font-weight: 700; }
.rv-ok   { color: #065F46; font-weight: 700; }
.rv-todo { color: #C0C4CC; font-weight: 500; font-style: italic; }

.doc-preview {
  border: 1px solid #EAECF0;
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  margin-bottom: 6px;
}
.doc-preview:last-child { margin-bottom: 0; }

/* doc-card wraps the .doc-preview header + a decision panel below.
   The whole stack shares one border so the doc and its review are
   visually one unit. */
.doc-card {
  border: 1px solid #EAECF0;
  border-radius: 10px;
  background: #fff;
  margin-bottom: 8px;
  overflow: hidden;
}
.doc-card:last-child { margin-bottom: 0; }
.doc-preview--in-card {
  border: 0;
  border-radius: 0;
  margin-bottom: 0;
}
.doc-preview-body { flex: 1 1 auto; min-width: 0; }
.doc-download-link {
  color: #16A37F;
  text-decoration: none;
  margin-left: 4px;
  font-weight: 600;
}
.doc-download-link:hover { text-decoration: underline; }

.doc-actions {
  border-top: 1px solid #F0F2F5;
  padding: 10px 12px;
  background: #F9FAFB;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.doc-comment-input {
  width: 100%;
  height: 34px;
  padding: 0 11px;
  border: 1px solid #EAECF0;
  border-radius: 7px;
  background: #fff;
  font-size: 12px;
  color: #0A0A0A;
  font-family: inherit;
  outline: none;
  transition: border-color 0.12s, box-shadow 0.12s;
}
.doc-comment-input::placeholder { color: #C0C4CC; }
.doc-comment-input:focus {
  border-color: #16A37F;
  box-shadow: 0 0 0 3px rgba(22, 163, 127, 0.08);
}

.doc-actions-buttons {
  display: flex;
  gap: 8px;
}
.doc-action-btn {
  flex: 1;
  height: 34px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: 0;
  transition: background 0.12s;
}
.doc-action-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.doc-action-approve { background: #16A37F; color: #fff; }
.doc-action-approve:hover:not(:disabled) { background: #138C6C; }
.doc-action-reject  { background: #FEF2F2; color: #991B1B; border: 1px solid #FECACA; }
.doc-action-reject:hover:not(:disabled)  { background: #FEE2E2; }

/* "You're allowed to look but not touch" state for the action panel —
   the controls are still rendered (so an MLRO can read the comment
   field while a Compliance review is in flight), but greyed out. */
.doc-actions-locked { opacity: 0.85; }
.doc-actions-locked-note {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #6B7280;
  background: #F3F4F6;
  border-radius: 6px;
  padding: 5px 9px;
  align-self: flex-start;
}

.doc-saved-comment {
  border-top: 1px solid #F0F2F5;
  background: #F9FAFB;
  padding: 8px 12px;
  font-size: 12px;
  color: #374151;
  border-left: 2px solid #E5E7EB;
}

/* Reviewed-file footer: status pill + actor + date, with the saved
   MLRO comment underneath when there is one. */
.doc-decision {
  border-top: 1px solid #F0F2F5;
  background: #F9FAFB;
  padding: 8px 12px 10px;
}
.doc-decision-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #374151;
}
.doc-decision-status {
  font-weight: 700;
  font-size: 11px;
  padding: 3px 9px;
  border-radius: 5px;
}
.doc-decision-actor { font-weight: 600; color: #0A0A0A; }
.doc-decision-when { color: #6B7280; }
.doc-decision-comment {
  margin-top: 6px;
  font-size: 12px;
  color: #374151;
  background: #fff;
  border-left: 2px solid #E5E7EB;
  padding: 6px 10px;
  border-radius: 0 6px 6px 0;
}
.doc-decision-tag { font-weight: 700; color: #6B7280; margin-right: 4px; }
/* Внутренняя причина — визуально отделяем (амбер), чтобы не спутать с клиентской. */
.doc-decision-internal { background: #FFFBEB; border-left-color: #FDE68A; color: #92400E; }
.doc-decision-internal .doc-decision-tag { color: #B45309; }

/* ---- Предыдущие (замещённые) решения по документу ---- */
.doc-past { margin-top: 8px; }
.doc-past-toggle {
  display: inline-flex; align-items: center; gap: 6px;
  background: none; border: none; padding: 2px 0; cursor: pointer;
  font-size: 12px; font-weight: 600; color: #6B7280;
}
.doc-past-toggle:hover { color: #374151; }
.doc-past-chev { font-size: 10px; width: 10px; }
.doc-past-list { margin-top: 6px; display: flex; flex-direction: column; gap: 8px; }
.doc-past-item { opacity: 0.92; }

/* ---- Request status timeline (modal) ---- */
.req-timeline {
  list-style: none;
  padding: 0;
  margin: 0;
}
.req-timeline-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-bottom: 1px solid #F0F2F5;
  background: #fff;
  border-radius: 7px;
  margin-bottom: 4px;
}
.req-timeline-row:last-child { border-bottom: 0; margin-bottom: 0; }
.req-timeline-dot {
  width: 10px; height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 5px;
  background: #D1D5DB;
}
.req-timeline-dot.req-pill-ok    { background: #16A37F; }
.req-timeline-dot.req-pill-bad   { background: #DC2626; }
.req-timeline-dot.req-pill-warn  { background: #F59E0B; }
.req-timeline-dot.req-pill-muted { background: #9CA3AF; }
.req-timeline-body { flex: 1 1 auto; min-width: 0; }
.req-timeline-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 2px;
}
.req-timeline-status { font-size: 13px; font-weight: 700; color: #0A0A0A; }
.req-timeline-when   { font-size: 11px; color: #9CA3AF; white-space: nowrap; }
.req-timeline-meta { font-size: 12px; color: #6B7280; }
.req-timeline-type {
  background: #F3F4F6;
  color: #374151;
  font-weight: 700;
  font-size: 11px;
  padding: 2px 8px;
  border-radius: 5px;
}
.req-timeline-actor { margin-left: 4px; }

/* Keep modal interactive even when the host UserDetail is in read-only
   mode (side-panel mount). The side-panel locks editing on the
   profile fields, but admin decisions on documents should still work. */
.user-detail-readonly .req-modal-overlay,
.user-detail-readonly .req-modal-overlay * { pointer-events: auto !important; }
.user-detail-readonly .req-modal button,
.user-detail-readonly .req-modal .btn { display: inline-flex !important; }
.user-detail-readonly .req-modal input,
.user-detail-readonly .req-modal textarea,
.user-detail-readonly .req-modal select {
  pointer-events: auto !important;
  background-color: #fff !important;
}
/* Same exception for the Actions tab: block/unblock and 2FA-reset
   buttons must stay clickable even in side-panel mount. */
.user-detail-readonly .action-grid button { display: inline-flex !important; }
.doc-preview-icon {
  width: 32px; height: 32px;
  border-radius: 7px;
  background: #F0F2F5;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  color: #9CA3AF;
}
.doc-preview-icon svg { width: 15px; height: 15px; }
.doc-preview-name { font-size: 13px; font-weight: 600; color: #0A0A0A; }
.doc-preview-index { font-size: 11px; font-weight: 600; color: #9CA3AF; margin-left: 4px; }
.doc-preview-meta { font-size: 11px; color: #9CA3AF; margin-top: 2px; }
.doc-preview-status {
  margin-left: auto;
  font-size: 11px; font-weight: 700;
  padding: 3px 9px;
  border-radius: 5px;
  white-space: nowrap;
}

.req-modal-fade-enter-active,
.req-modal-fade-leave-active { transition: opacity 0.18s ease; }
.req-modal-fade-enter,
.req-modal-fade-leave-to { opacity: 0; }

@media (max-width: 720px) {
  .req-modal-body { grid-template-columns: 1fr; }
  .req-modal-section.full { grid-column: span 1; }
  .req-modal-fields { grid-template-columns: 1fr; }
  .rmf.full { grid-column: span 1; }
}

/* ---- Narrow viewports: stack fields ---- */
@media (max-width: 560px) {
  .sp-fields { grid-template-columns: 1fr; }
  .sp-status { grid-template-columns: 1fr 1fr; }
}
</style>
